<template>
	<article>
		<div id="map" />
		<LoadingOverlay :visible="isLoadingInitialOpportunities" />
		<ClickPulse
			:point="pulse.point"
			:visible="pulse.visible"
			:animation-key="pulse.key"
			:data-tour="parcelHintFeatureId != null ? 'parcel-select' : undefined"
		/>

		<div id="draw-info" ref="drawInfoBox" hidden>
			<h1 class="feature-type">
				<span v-if="drawInfo.type === drawModes.DRAW_POLYGON">Area</span>
				<span v-if="drawInfo.type === drawModes.DRAW_LINE_STRING">Distance</span>
			</h1>
			<span v-if="drawInfo.type === drawModes.DRAW_POLYGON" class="value">
				{{ formatQuantity(area) }} {{ areaUnitSymbol }}
			</span>
			<span v-if="drawInfo.type === drawModes.DRAW_LINE_STRING" class="value">
				{{ formatQuantity(distance) }} {{ distanceUnitSymbol }}
			</span>
		</div>

		<map-search
			class="map-search"
			data-tour="map-search"
			:map-center="mapCenter"
			:horizontal-offset="horizontalOffset"
			v-model:selected-feature-id="selectedFeatureId"
			@search-by-address="handleSearchByAddress"
			@search-by-parcel="handleSearchByParcel"
			@search-by-transaction="handleSearchByTransaction"
			@search-by-opportunity="handleSearchByOpportunityId"
		>
			<template #suggestion-type-title="{ item }">{{ $t(`search.${item}`) }}</template>
		</map-search>

		<map-controls
			data-tour="map-controls"
			:current-style="currentStyle"
			:bearing="mapBearing"
			:pitch="mapPitch"
			:measuring="mapMeasuring"
			:right-offset="rightOffset"
			@update:current-style="style => emit('current-style', style)"
			@measure="startMeasure"
			@clear-measures="clearMeasures"
			@reset-north="resetNorth"
			@rotate="rotateCamera"
			@set-attribution="setAttribution"
		/>
	</article>
</template>

<script setup lang="ts">
import MapSearch from "@/components/map/MapSearch.vue"
import type { AddressPosition, ParcelPosition } from "@/components/map/MapSearch.vue"
import MapControls from "@/components/map/MapControls.vue"
import * as Sentry from "@sentry/vue"
import { RESIDENTIAL_UNITS_SLIDER_MAX, type MapStyleValue } from "@/components/constants"
import { BASE_MAP_STYLES, isSatelliteStyle } from "@/components/map/baseMapStyles"
import { addParcels, ParcelsLayerId, ParcelsTheme, PARCEL_SOURCE_LAYER } from "@/components/map/parcelsLayer"
import {
	addEndangeredSpecies,
	addFloodableZones,
	addWetlands,
	endangeredSpeciesLayerDefinitions,
	getActiveEndangeredSpeciesFilter,
	getDisabledEndangeredSpeciesFilter,
	getEndangeredSpeciesFilter,
	getEndangeredSpeciesLabelField,
} from "@/components/map/environmentLayers"
import { addPlanningPolicy, addZoning, ZONING_SOURCE_LAYER, ZONING_SOURCE_NAME } from "@/components/map/planningLayers"
import {
	addAgriculturalZones,
	addSoilCapabilityForAgricultureFill,
	addSoilCapabilityForAgricultureLabels,
	addSoilCapabilityForAgricultureLine,
} from "@/components/map/agriculturalLayers"
import { loadHatchPatterns } from "@/components/map/layerPatterns"
import { addTransitLines, addTransitStations, addTransitStationsLabels } from "@/components/map/transitLayers"
import { UseRootStore, Permission, type User, type MapboxCameraPositionHash, type Filters } from "@/model/RootStore"
import { useProspectStore } from "@/stores/prospectStore.ts"
import { useUrbanPrioritiesStore } from "@/stores/urbanPrioritiesStore.ts"
import {
	Borough,
	LayerType,
	type LayerTypeValue,
	LayerFeatureType,
	type LayerFeatureSpec,
	type LayerFeatureDetail,
	type Transaction,
	type Opportunity,
	type Project,
} from "@/model/DataModel.ts"
import { OpportunityStatus, OpportunityType, ProjectType } from "@/graphql"
import {
	IconBadge,
	buildBadgeIconDataUri,
	buildOpportunityIconSvg,
	iconName,
	resolveIconFamily,
} from "@/utils/opportunityIcons"
import { isOpportunityType } from "@/utils/opportunityType"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { AreaUnit, DistanceUnit } from "@/utils/unitSystem"
import { formatQuantity } from "@/utils/format"
import { config, Feature, type UI } from "@/AppConfig.ts"
import { useMediaQuery } from "@vueuse/core"
import { storeToRefs } from "pinia"
import { tileRequestHeaders } from "@/utils/tileRequestHeaders"
import { cadastreFilterForView } from "@/utils/cadastreFilter"
import { transactionParkingFilter } from "./transactionParkingFilters"
import { builtAssetBuckets, builtSubcategoryBuckets } from "@/utils/builtAssetClassification"
import { transactionBuildingPriceFilter } from "./transactionBuildingPriceFilter"
import { transactionRentalIncomeFilter } from "./transactionRentalIncomeFilter"
import { useToastStore } from "@/stores/toastStore"
import { ParcelHintStatus, useUIStore } from "@/stores/uiStore"
import { IconName } from "@/components/icons/registry"
import {
	type Ref,
	onMounted,
	ref,
	computed,
	reactive,
	shallowRef,
	toRef,
	toRaw,
	watch,
	inject,
	nextTick,
	onBeforeUnmount,
} from "vue"
import mapboxgl from "mapbox-gl"
import type { MapboxGeoJSONFeature, MapMouseEvent, MapTouchEvent } from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import MapboxDraw from "@mapbox/mapbox-gl-draw"
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css"
import { default as getArea } from "@turf/area"
import { default as getLength } from "@turf/length"
import { isHuntingMode, ProjectStoreKey } from "@/stores/projectStore.ts"
import { useOpportunityStore } from "@/stores/opportunityStore"
import { useNavigationContext } from "@/composables/useNavigationContext"
import { useView } from "@/composables/useView"
import { RANGE_MAX, TransactionVerificationStatus, ViewScope, type View } from "@/types"
import ClickPulse from "./ClickPulse.vue"
import LoadingOverlay from "@/components/ui/LoadingOverlay.vue"

declare global {
	interface Window {
		map: mapboxgl.Map
		//@ts-ignore
		store: ReturnType<typeof UseRootStore>
	}
}

console.info("Mapbox version ", mapboxgl.version)

type DrawModeType = (typeof MapboxDraw.constants.modes)[keyof typeof MapboxDraw.constants.modes]

type CustomMapMouseEvent = (MapMouseEvent | MapTouchEvent) & { stopMapPropagation?: boolean }

type CustomLayer = mapboxgl.Layer & { metadata: { custom: boolean } }
type TransactionTileFilter = mapboxgl.FilterSpecification

function hasDefinedNumberRange(
	range: { min?: number | null; max?: number | null } | undefined,
	defaultMax: number | null
): boolean {
	return (range?.min ?? 0) !== 0 || (range?.max ?? defaultMax) !== defaultMax
}

function numberRangeTileFilter(
	property: string,
	range: { min?: number | null; max?: number | null } | undefined
): unknown[] | true {
	if (!hasDefinedNumberRange(range, RANGE_MAX)) return true

	return [
		"all",
		["has", property],
		["!=", ["get", property], null],
		[">=", ["to-number", ["get", property]], range?.min ?? 0],
		["<=", ["to-number", ["get", property]], range?.max ?? RANGE_MAX],
	]
}

function buildingAgeTileFilter(range: { min?: number | null; max?: number | null } | undefined): unknown[] | true {
	if (!hasDefinedNumberRange(range, RANGE_MAX)) return true

	const currentYear = new Date().getFullYear()
	return [
		"all",
		["has", "construction_year_min"],
		["has", "construction_year_max"],
		["<=", ["to-number", ["get", "construction_year_min"]], currentYear - (range?.min ?? 0)],
		[">=", ["to-number", ["get", "construction_year_max"]], currentYear - (range?.max ?? RANGE_MAX)],
	]
}

function propertyUseTileFilter(prefixes: string[]): unknown[] | true {
	if (prefixes.length === 0) return true

	return [
		"any",
		...prefixes.flatMap(prefix => [
			["in", `{${prefix}`, ["to-string", ["get", "property_use_codes"]]],
			["in", `,${prefix}`, ["to-string", ["get", "property_use_codes"]]],
		]),
	]
}

function hasDefinedDateRange(range: { min?: Date | string | null; max?: Date | string | null } | undefined): boolean {
	return range?.min != null || range?.max != null
}

enum MapSourceId {
	Cadastre = "cadastre-source",
	CompletedAssessments = "completed-assessments-source",
	Opportunities = "opportunities-source",
	Prospects = "prospects-source",
	Transactions = "transactions",
}

enum MapLayerId {
	AgriculturalZones = "agricultural-zones",
	Boroughs = "administrative-divisions/boroughs",
	MetropolitanAreas = "administrative-divisions/metropolitan-areas",
	Wetlands = "wetlands",
}

enum MapEventName {
	SourceData = "sourcedata",
}

// Type guard to assert a layer is custom
function isCustomLayer(layer: mapboxgl.Layer): layer is CustomLayer {
	return Boolean(layer.metadata) && (layer.metadata as any).custom === true
}

// Helper function that wraps map.querySourceFeatures so that the correct type
// is inferred.
function querySourceFeatures(
	map: mapboxgl.Map,
	sourceId: string,
	params: Parameters<mapboxgl.Map["querySourceFeatures"]>[1]
): MapboxGeoJSONFeature[] {
	return map.querySourceFeatures(sourceId, params ?? {}) as MapboxGeoJSONFeature[]
}

const drawModes = ref(MapboxDraw.constants.modes)
const selectedParcelIDs = ref<string[]>([])
const parcelMarkers = ref(new Map())

const isCtrlPressed = ref(false)
const selectionModeActive = ref(false)
const totalSqFt = ref(0)
const totalSqMeter = ref(0)

let preventFlyTo = false
let pendingTransactionSourceLoadFilterRefresh: ((event: mapboxgl.MapSourceDataEvent) => void) | null = null
let pendingCadastreSelectionRestore: ((event: mapboxgl.MapSourceDataEvent) => void) | null = null

function findRenderedCadastreFeature(
	map: mapboxgl.Map,
	center: [number, number],
	parcelId: string
): MapboxGeoJSONFeature | undefined {
	const point = map.project(center)
	const features = map.queryRenderedFeatures(point, {
		layers: ["cadastre"],
	})
	return features.find(f => String(f.properties?.parcel_id) === parcelId)
}

window.addEventListener("keydown", e => {
	if (e.key === "Control" || e.metaKey) {
		isCtrlPressed.value = true
	}
})

window.addEventListener("keyup", e => {
	if (e.key === "Control" || e.key === "Meta") {
		isCtrlPressed.value = false
	}
})

function refreshSource(map: mapboxgl.Map, sourceId: string): void {
	// Get the existing source object
	const source = map.getSource(sourceId)
	if (!source) {
		console.error(`Source with id '${sourceId}' not found`)
		return
	}
	const style = map.getStyle()!

	// Get all layers that use this source
	const layers = style.layers.filter(layer => layer.source === sourceId)

	// Save the source data
	const sourceData = style.sources[sourceId]

	// Remove the source and its layers
	layers.forEach(layer => map.removeLayer(layer.id))
	map.removeSource(sourceId)

	// Re-add the source
	map.addSource(sourceId, sourceData)

	// Re-add the layers
	layers.forEach(layer => map.addLayer(layer))
}

const TILES_URL_LIVE = import.meta.env.VITE_TILES_URL_LIVE
const TILES_URL_DAILY = import.meta.env.VITE_TILES_URL_DAILY
const PUBLIC_TILES_URL_LIVE = import.meta.env.VITE_PUBLIC_TILES_URL_LIVE || TILES_URL_LIVE

const OPPORTUNITY_LAYER_ID = "opportunities-icons"
const OPPORTUNITY_CLUSTER_SHADOW_LAYER_ID = "opportunity-cluster-shadows"
const OPPORTUNITY_CLUSTER_LAYER_ID = "opportunity-clusters"
const OPPORTUNITY_CLUSTER_COUNT_LAYER_ID = "opportunity-cluster-counts"
const OPPORTUNITY_NEW_BADGE_LAYER_ID = "opportunities-badge-new"
const OPPORTUNITY_OFFER_BADGE_LAYER_ID = "opportunities-badge-offer"
const OPPORTUNITY_LIKED_BADGE_LAYER_ID = "opportunities-badge-liked"
const OPPORTUNITY_DISLIKED_BADGE_LAYER_ID = "opportunities-badge-disliked"
const OPPORTUNITY_INTERACTIVE_LAYER_IDS = [
	OPPORTUNITY_LAYER_ID,
	OPPORTUNITY_NEW_BADGE_LAYER_ID,
	OPPORTUNITY_OFFER_BADGE_LAYER_ID,
	OPPORTUNITY_LIKED_BADGE_LAYER_ID,
	OPPORTUNITY_DISLIKED_BADGE_LAYER_ID,
]
const OPPORTUNITY_RENDER_LAYER_IDS = [
	OPPORTUNITY_LAYER_ID,
	OPPORTUNITY_CLUSTER_SHADOW_LAYER_ID,
	OPPORTUNITY_CLUSTER_LAYER_ID,
	OPPORTUNITY_CLUSTER_COUNT_LAYER_ID,
	OPPORTUNITY_NEW_BADGE_LAYER_ID,
	OPPORTUNITY_OFFER_BADGE_LAYER_ID,
	OPPORTUNITY_LIKED_BADGE_LAYER_ID,
	OPPORTUNITY_DISLIKED_BADGE_LAYER_ID,
]
const OPPORTUNITY_SOURCE_LAYER = "tiles.opportunity"
const OPPORTUNITY_PUBLIC_SOURCE_LAYER = "tiles.opportunity_public"
const OPPORTUNITY_NEW_BADGE_IMAGE_ID = "opportunity-badge-new"
const OPPORTUNITY_OFFER_BADGE_IMAGE_ID = "opportunity-badge-offer"
const OPPORTUNITY_LIKED_BADGE_IMAGE_ID = "opportunity-badge-liked"
const OPPORTUNITY_DISLIKED_BADGE_IMAGE_ID = "opportunity-badge-disliked"
const NEW_OPPORTUNITY_BADGE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000
const OPPORTUNITY_ICON_SIZE = 0.7
const OPPORTUNITY_ICON_SIZE_SELECTED = 0.87
const OPPORTUNITY_ICON_OPACITY_DIMMED = 0.6
const OPPORTUNITY_CLIENT_FILTER_MIN_ZOOM = 12
const OPPORTUNITY_TYPE_ICON_NAMES = Object.fromEntries(
	Object.values(OpportunityType).map(type => {
		const resolvedFamily = resolveIconFamily(type)
		return [type, resolvedFamily ? iconName(resolvedFamily.family, []) : "listing"]
	})
) as Record<OpportunityType, string>
let currentOpportunityTileUrl: string | null = null
let lastOpportunityClick: { id: string; x: number; y: number; time: number } | null = null
const CITY_PV_DOCUMENTS_SOURCE_NAME = "city-pv-documents-source"
const CITY_PV_DOCUMENTS_SOURCE_LAYER = "gold.city_pv_documents"
const CITY_PV_DOCUMENTS_COLOR = "#0b6e4f"
const COMPLETED_ASSESSMENTS_COLOR = "#B33C82"
const CODEPENDENT_LAYER_IDS = new Set([
	ParcelsLayerId.Line,
	"administrative-divisions/regions_line",
	"administrative-divisions/region_labels",
	"administrative-divisions/rcms_line",
	"administrative-divisions/rcm_labels",
	"administrative-divisions/metropolitan-areas_line",
	"administrative-divisions/municipality_labels",
	"administrative-divisions/boroughs_line",
	"administrative-divisions/postal-code_labels",
	"wetlands_line",
	"agricultural-zones_line",
	"planning-policy-hatch",
	"planning-policy-line",
	"planning-policy-hit",
	"soil-capability_line",
	"soil-capability_labels",
	"endangered-species-labels",
	"transit-stations_labels",
	"zoning-outline",
	"zoning-labels",
	"city-pv-documents-halo",
	`${LayerType.CompletedAssessments}-halo`,
])

function usesStandardStyle(map: mapboxgl.Map): boolean {
	return map.getStyle()!.imports?.[0]?.id === "basemap"
}

function usesSatelliteStyle(map: mapboxgl.Map): boolean {
	return map.getStyle()!.name === "Mapbox Satellite Streets"
}

const props = defineProps<{
	currentStyle: MapStyleValue
	horizontalOffset?: number
	rightOffset?: string
	focusedMunicipalityCode?: string | null
}>()

const emit = defineEmits([
	"loaded",
	"opportunity-selected",
	"layer-features",
	"error",
	"viewport-opportunities-changed",
	"viewport-opportunity-count-changed",
	"current-style",
])

const store = UseRootStore()
const toastStore = useToastStore()
const uiStore = useUIStore()
const {
	hoveredOpportunityId,
	selectedOpportunityId,
	selectedParcelID,
	selectedParcel,
	selectedParcels,
	visibleLayers,
	cameraPosition,
	opportunities: storeOpportunities,
	previewedTransaction,
	selectedTransaction,
	filters,
	currentLocale,
	layers,
} = storeToRefs(store)

const prospectStore = useProspectStore()
const { prospects } = storeToRefs(prospectStore)
const urbanPrioritiesStore = useUrbanPrioritiesStore()
const { urbanPriorities } = storeToRefs(urbanPrioritiesStore)

const currentStyle = toRef(props, "currentStyle")
// TODO verify if still used
const selectedFeatureId = ref<string | null>(null)
const marker = new mapboxgl.Marker({ color: "#315e26" })

function isLayerVisible(map: mapboxgl.Map, layerId: string) {
	try {
		if (!map.getLayer(layerId)) return false
		return map.getLayoutProperty(layerId, "visibility") === "visible"
	} catch {
		return false
	}
}

const isLargeScreen = inject<Ref<boolean>>("isLargeScreen")!
const projectStore = inject(ProjectStoreKey)!()
const { currentProject } = storeToRefs(projectStore)
const opportunityStore = useOpportunityStore()
const { projectOpportunitiesInvalidation } = storeToRefs(opportunityStore)
const opportunityView = useView(ViewScope.OpportunitiesMap)
const opportunityHuntingView = useView(ViewScope.OpportunitiesHuntingMap)
const parcelView = useView(ViewScope.ParcelsMap)
const parcelHuntingView = useView(ViewScope.ParcelsHuntingMap)
const transactionView = useView(ViewScope.TransactionsMap)
const transactionHuntingView = useView(ViewScope.TransactionsHuntingMap)
const { isMapMode, isHuntingSection } = useNavigationContext({ currentProject })
const isHuntingMapMode = computed(() => isMapMode.value && isHuntingSection.value)
const activeOpportunityView = computed(() => (isHuntingMapMode.value ? opportunityHuntingView : opportunityView))
const activeParcelView = computed(() => (isHuntingMapMode.value ? parcelHuntingView : parcelView))
const activeTransactionView = computed(() => (isHuntingMapMode.value ? transactionHuntingView : transactionView))
const defaultOpportunityMapTypes = computed(() =>
	isHuntingMapMode.value
		? [
				OpportunityType.ListingLanderz,
				OpportunityType.ListingPotential,
				OpportunityType.OffMarketOfficial,
				OpportunityType.OpportunityTarget,
			]
		: [OpportunityType.ListingLanderz, OpportunityType.ListingPotential, OpportunityType.OffMarketOfficial]
)
const activeOpportunityMapTypes = computed(() => {
	const types = (
		activeOpportunityView.value.view.value.filter?.opportunity?.opportunityTypes ?? defaultOpportunityMapTypes.value
	).filter(isOpportunityType)
	// hack-project: targets are never shown outside hunting mode, even if a persisted filter says otherwise
	return isHuntingMode() ? types : types.filter(type => type !== OpportunityType.OpportunityTarget)
})

const mapRef = shallowRef<mapboxgl.Map | null>(null)
const mapCenter = ref({ lng: 0, lat: 0 })
const mapStyleReadyTick = ref(0)

const isLoadingInitialOpportunities = ref(false)

watch(
	() => props.focusedMunicipalityCode,
	municipalityCode => {
		if (mapRef.value) applyMunicipalityFocus(mapRef.value, municipalityCode)
	}
)

// mapbox only tracks window resizes on its own; on the mobile SideNav layout, switching
// between the desktop side nav and the mobile bottom nav resizes the map's container without
// a window resize, so the canvas needs an explicit resize() call or it keeps stale dimensions.
// Scoped strictly to that mobile+flag case so desktop/tablet layout is never touched.
const isMobileSideNavViewport = useMediaQuery("(max-width: 768px)")
let mapContainerResizeObserver: ResizeObserver | null = null

function teardownMapContainerResizeObserver(): void {
	mapContainerResizeObserver?.disconnect()
	mapContainerResizeObserver = null
}

watch(
	[mapRef, isMobileSideNavViewport],
	([map, isMobileSideNavViewportValue]) => {
		if (!map || !isMobileSideNavViewportValue) {
			teardownMapContainerResizeObserver()
			return
		}

		if (mapContainerResizeObserver) return

		const container = document.getElementById("map")
		if (!container) return

		mapContainerResizeObserver = new ResizeObserver(() => map.resize())
		mapContainerResizeObserver.observe(container)
	},
	{ immediate: true }
)

const pulse = reactive({
	lngLat: null as { lng: number; lat: number } | null,
	point: null as { x: number; y: number } | null,
	visible: false,
	key: 0,
})

const pulseMoveHandler = () => refreshPulsePoint()
function clearPulse() {
	pulse.visible = false
	pulse.lngLat = null
	pulse.point = null
}

function refreshPulsePoint() {
	const map = mapRef.value
	if (!map || !pulse.visible || !pulse.lngLat) return
	const p = map.project(pulse.lngLat)
	pulse.point = { x: p.x, y: p.y }
}

const PARCEL_HINT_MIN_ZOOM = 16
const PARCEL_HINT_SEARCH_RADIUS = 160
const PARCEL_HINT_SEARCH_STEP = 20

const parcelHintFeatureId = ref<string | number | null>(null)

interface ParcelHint {
	featureId: string | number
	point: mapboxgl.Point
}

function findParcelNearCenter(map: mapboxgl.Map): ParcelHint | null {
	const canvas = map.getCanvas()
	const center = new mapboxgl.Point(canvas.clientWidth / 2, canvas.clientHeight / 2)
	const hitsByFeature = new Map<string | number, mapboxgl.Point[]>()
	let closest: { featureId: string | number; distance: number } | null = null

	for (let dx = -PARCEL_HINT_SEARCH_RADIUS; dx <= PARCEL_HINT_SEARCH_RADIUS; dx += PARCEL_HINT_SEARCH_STEP) {
		for (let dy = -PARCEL_HINT_SEARCH_RADIUS; dy <= PARCEL_HINT_SEARCH_RADIUS; dy += PARCEL_HINT_SEARCH_STEP) {
			const point = center.add(new mapboxgl.Point(dx, dy))
			const featureId = map.queryRenderedFeatures(point, { layers: ["cadastre"] })[0]?.id
			if (featureId == null) continue

			hitsByFeature.set(featureId, [...(hitsByFeature.get(featureId) ?? []), point])
			const distance = point.dist(center)
			if (!closest || distance < closest.distance) closest = { featureId, distance }
		}
	}
	if (!closest) return null

	const hits = hitsByFeature.get(closest.featureId)!
	const average = hits.reduce((sum, hit) => sum.add(hit), new mapboxgl.Point(0, 0)).div(hits.length)
	const point = hits.reduce((best, hit) => (hit.dist(average) < best.dist(average) ? hit : best))
	return { featureId: closest.featureId, point }
}

function setParcelHintFeatureState(map: mapboxgl.Map, featureId: string | number, tourHint: boolean): void {
	map.setFeatureState({ source: MapSourceId.Cadastre, sourceLayer: PARCEL_SOURCE_LAYER, id: featureId }, { tourHint })
}

function placeParcelHint(map: mapboxgl.Map): void {
	if (uiStore.parcelHintStatus !== ParcelHintStatus.Requested) return

	const hint = findParcelNearCenter(map)
	if (!hint) {
		uiStore.parcelHintStatus = ParcelHintStatus.NotFound
		return
	}

	parcelHintFeatureId.value = hint.featureId
	setParcelHintFeatureState(map, hint.featureId, true)
	pulse.lngLat = map.unproject(hint.point)
	pulse.point = { x: hint.point.x, y: hint.point.y }
	pulse.visible = true
	pulse.key++
	uiStore.parcelHintStatus = ParcelHintStatus.Shown
}

function showParcelHint(): void {
	const map = mapRef.value
	if (!map) {
		uiStore.parcelHintStatus = ParcelHintStatus.NotFound
		return
	}

	store.ensureVisibleLayer("cadastre")
	map.once("idle", () => placeParcelHint(map))
	if (map.getZoom() < PARCEL_HINT_MIN_ZOOM) map.easeTo({ zoom: PARCEL_HINT_MIN_ZOOM })
	else map.triggerRepaint()
}

function hideParcelHint(): void {
	const map = mapRef.value
	if (parcelHintFeatureId.value == null) return
	if (map) setParcelHintFeatureState(map, parcelHintFeatureId.value, false)
	parcelHintFeatureId.value = null
	clearPulse()
}

watch(
	() => uiStore.parcelHintStatus,
	status => {
		if (status === ParcelHintStatus.Requested) showParcelHint()
		if (status === ParcelHintStatus.Inactive) hideParcelHint()
	}
)

function setFeatureDetails(details: LayerFeatureDetail[]) {
	emit("layer-features", details)
	if (details.length === 0) {
		clearPulse()
		if (mapRef.value) setSelectedZoningFeature(mapRef.value)
	}
}

const drawInfo = ref<{
	type: DrawModeType | null
	areaSquareMeters: number
	distanceMeters: number
}>({
	type: null,
	areaSquareMeters: 0,
	distanceMeters: 0,
})
const {
	areaUnit,
	areaUnitSymbol,
	convertAreaToPreferredUnit,
	distanceUnit,
	distanceUnitSymbol,
	convertDistanceToPreferredUnit,
} = useUnitSystem()
const area = computed(() => {
	const convertedArea = convertAreaToPreferredUnit(drawInfo.value.areaSquareMeters, AreaUnit.SquareMeters)

	return areaUnit.value === AreaUnit.SquareMeters ? Math.round(convertedArea * 100) / 100 : Math.round(convertedArea)
})
const distance = computed(() => {
	const convertedDistance = convertDistanceToPreferredUnit(drawInfo.value.distanceMeters, DistanceUnit.Meters)

	return distanceUnit.value === DistanceUnit.Meters
		? Math.round(convertedDistance * 100) / 100
		: Math.round(convertedDistance)
})
const drawInfoBox = shallowRef<HTMLElement | null>(null)
const drawInstance = shallowRef<MapboxDraw | null>(null)
const mapBearing = ref(0)
const mapPitch = ref(0)
const mapMeasuring = ref(false)

function resetNorth(): void {
	mapRef.value?.resetNorthPitch()
}

function rotateCamera({ bearing, pitch }: { bearing: number; pitch: number }): void {
	const map = mapRef.value
	if (!map) return
	map.setBearing(bearing)
	map.setPitch(pitch)
}

function setAttribution(open: boolean): void {
	const attrib = mapRef.value?.getContainer().querySelector(".mapboxgl-ctrl-attrib")
	attrib?.classList.toggle("mapboxgl-compact-show", open)
}

function startMeasure(kind: "distance" | "area"): void {
	const draw = drawInstance.value
	if (!draw) return
	draw.changeMode(
		kind === "distance" ? MapboxDraw.constants.modes.DRAW_LINE_STRING : MapboxDraw.constants.modes.DRAW_POLYGON
	)
}

function clearMeasures(): void {
	const draw = drawInstance.value
	if (!draw) return
	draw.deleteAll()
	drawInfo.value.areaSquareMeters = 0
	drawInfo.value.distanceMeters = 0
	if (drawInfoBox.value) drawInfoBox.value.hidden = true
}

// TODO use TS interface, when available, to describe feature objects

// featureids of parcels highlighted when selecting opportunity
const opportunityHighlightedFeatureIds: (string | number)[] = []
let pendingOpportunityParcelsHighlight: (() => void) | null = null

// featureid of the parcel currently hovered, if any
let hoveredParcelFeatureId: string | number | null = null
let hoveredCompletedAssessmentFeatureId: string | number | null = null
let hoveredZoningFeatureId: string | number | null = null
let selectedZoningFeatureId: string | number | null = null

function setZoningFeatureState(
	map: mapboxgl.Map,
	id: string | number | null,
	state: { hover?: boolean; selected?: boolean }
): void {
	if (id === null || !map.getSource(ZONING_SOURCE_NAME)) return
	map.setFeatureState({ source: ZONING_SOURCE_NAME, sourceLayer: ZONING_SOURCE_LAYER, id }, state)
}

function setHoveredZoningFeature(map: mapboxgl.Map, id: string | number | null): void {
	if (hoveredZoningFeatureId === id) return
	setZoningFeatureState(map, hoveredZoningFeatureId, { hover: false })
	hoveredZoningFeatureId = id
	setZoningFeatureState(map, hoveredZoningFeatureId, { hover: true })
}

function setSelectedZoningFeature(map: mapboxgl.Map, feature?: MapboxGeoJSONFeature): void {
	const nextId = feature?.id ?? feature?.properties?.zone_code ?? null
	if (selectedZoningFeatureId === nextId) return
	setZoningFeatureState(map, selectedZoningFeatureId, { selected: false })
	selectedZoningFeatureId = nextId
	setZoningFeatureState(map, selectedZoningFeatureId, { selected: true })
}

function clearOpportunityParcelsHighlight(map: mapboxgl.Map): void {
	if (pendingOpportunityParcelsHighlight) {
		map.off("idle", pendingOpportunityParcelsHighlight)
		pendingOpportunityParcelsHighlight = null
	}
	if (map.getSource(MapSourceId.Cadastre)) {
		const selectedIds = new Set(selectedParcelIDs.value.map(String))
		for (const id of opportunityHighlightedFeatureIds) {
			if (selectedIds.has(String(id))) continue
			map.setFeatureState({ source: MapSourceId.Cadastre, sourceLayer: PARCEL_SOURCE_LAYER, id }, { highlight: false })
		}
	}
	opportunityHighlightedFeatureIds.length = 0
}

function findOpportunityParcelFeatureIds(map: mapboxgl.Map, opportunity: Opportunity): (string | number)[] {
	const parcelIds = new Set((opportunity.lot_numbers_full ?? []).map(String))
	if (parcelIds.size === 0) {
		if (!opportunity.latitude || !opportunity.longitude) return []
		const point = map.project([opportunity.longitude, opportunity.latitude])
		const featureId = map.queryRenderedFeatures(point, { layers: ["cadastre"] })[0]?.id
		return featureId == null ? [] : [featureId]
	}

	const features = querySourceFeatures(map, MapSourceId.Cadastre, { sourceLayer: PARCEL_SOURCE_LAYER })
	const featureIds = features
		.filter(feature => feature.id != null && parcelIds.has(String(feature.properties?.parcel_id)))
		.map(feature => feature.id!)
	return [...new Set(featureIds)]
}

function highlightOpportunityParcels(map: mapboxgl.Map): void {
	clearOpportunityParcelsHighlight(map)

	const selectedId = String(selectedOpportunityId.value)
	const opportunity =
		storeOpportunities.value.find((opportunity: Opportunity) => String(opportunity.id) === selectedId) ??
		viewportOpportunityCache.get(selectedId)
	if (!opportunity) return

	const expectedCount = new Set(opportunity.lot_numbers_full ?? []).size || 1

	const highlightLoadedParcels = () => {
		if (!map.getSource(MapSourceId.Cadastre)) return
		for (const id of findOpportunityParcelFeatureIds(map, opportunity)) {
			if (opportunityHighlightedFeatureIds.includes(id)) continue
			map.setFeatureState({ source: MapSourceId.Cadastre, sourceLayer: PARCEL_SOURCE_LAYER, id }, { highlight: true })
			opportunityHighlightedFeatureIds.push(id)
		}
		if (opportunityHighlightedFeatureIds.length >= expectedCount && pendingOpportunityParcelsHighlight) {
			map.off("idle", pendingOpportunityParcelsHighlight)
			pendingOpportunityParcelsHighlight = null
		}
	}

	pendingOpportunityParcelsHighlight = highlightLoadedParcels
	map.on("idle", highlightLoadedParcels)
	highlightLoadedParcels()
}

const clearHighlights = (map: mapboxgl.Map) => {
	clearOpportunityParcelsHighlight(map)

	const features = querySourceFeatures(map, MapSourceId.Cadastre, {
		sourceLayer: PARCEL_SOURCE_LAYER,
	})

	// We check first to see if we need to clear EVERYTHING or only a single selected parcel
	// If we have a single selected parcel, we clear only that one
	if (!selectedParcelIDs.value.length && selectedFeatureId.value !== null) {
		// Single search-based selection
		map.setFeatureState(
			{
				source: MapSourceId.Cadastre,
				sourceLayer: PARCEL_SOURCE_LAYER,
				id: selectedFeatureId.value,
			},
			{ highlight: false }
		)
		return
	}

	// Otherwise, we clear all selected parcels
	for (const parcelId of selectedParcelIDs.value) {
		const feature = features.find((f: MapboxGeoJSONFeature) => f.properties?.parcel_id === parcelId)
		if (feature?.id != null) {
			map.setFeatureState(
				{
					source: MapSourceId.Cadastre,
					sourceLayer: PARCEL_SOURCE_LAYER,
					id: feature.id,
				},
				{ highlight: false }
			)
		}
	}
}

const clearSelections = () => {
	if (mapRef.value) clearHighlights(mapRef.value)
	selectedFeatureId.value = null
	selectedOpportunityId.value = null
	selectedParcelID.value = null
	selectedParcel.value = null
	selectedParcelIDs.value = []
	selectedParcels.value = []
	// Clear anything already
	marker.remove()
	for (const marker of parcelMarkers.value.values()) {
		marker.remove()
	}
	parcelMarkers.value.clear()
}

function exitSelectionMode() {
	clearSelections()
	selectionModeActive.value = false
}

function addParcelToSelection(map: mapboxgl.Map, event: any, feature: any, parcelId: string) {
	map.setFeatureState(
		{
			source: MapSourceId.Cadastre,
			sourceLayer: PARCEL_SOURCE_LAYER,
			id: feature.id,
		},
		{ highlight: true }
	)

	const lat = event.lngLat.lat
	const lng = event.lngLat.lng
	const newMarker = new mapboxgl.Marker({ color: "#315e26" }).setLngLat({ lat, lng }).addTo(map)
	parcelMarkers.value.set(parcelId, newMarker)

	selectedParcelIDs.value.push(parcelId)
	selectedParcelIDs.value = [...selectedParcelIDs.value]

	selectedParcelID.value = parcelId
	selectedParcel.value = feature.properties
	selectedParcels.value.push(feature.properties)
	selectedFeatureId.value = feature.id
}

function selectParcel(map: mapboxgl.Map, event: any) {
	const feature = structuredClone(event.features[0])
	const parcelId = String(feature.properties["parcel_id"])

	clearSelections()
	addParcelToSelection(map, event, feature, parcelId)
}

function toggleParcelSelection(map: mapboxgl.Map, event: any) {
	const feature = structuredClone(event.features[0])
	const parcelId = String(feature.properties["parcel_id"])

	const index = selectedParcelIDs.value.indexOf(parcelId)
	if (index > -1) {
		selectedParcelIDs.value.splice(index, 1)
		selectedParcelIDs.value = [...selectedParcelIDs.value]

		selectedParcels.value.splice(index, 1)

		map.setFeatureState(
			{
				source: MapSourceId.Cadastre,
				sourceLayer: PARCEL_SOURCE_LAYER,
				id: feature.id,
			},
			{ highlight: false }
		)
		const marker = parcelMarkers.value.get(parcelId)
		if (marker) {
			marker.remove()
			parcelMarkers.value.delete(parcelId)
		}
		const nextSelectedParcelId = selectedParcelIDs.value.at(-1) ?? null
		selectedParcelID.value = nextSelectedParcelId
		selectedParcel.value = selectedParcels.value.at(-1) ?? null
		return
	}

	addParcelToSelection(map, event, feature, parcelId)
}

// Feature-state calls can race with style reloads (`map.setStyle`), during which
// a source may be temporarily missing. Use this helper to apply state now when
// possible, or retry once the source is available again on `styledata`.
function setFeatureStateWhenSourceReady(
	map: mapboxgl.Map,
	target: { source: string; sourceLayer?: string; id: number | string },
	state: Record<string, any>
) {
	const apply = () => {
		map.setFeatureState(
			{
				source: target.source,
				sourceLayer: target.sourceLayer,
				id: target.id,
			},
			{ ...state }
		)
	}

	if (map.getSource(target.source)) {
		apply()
		return
	}

	const onStyleData = () => {
		if (!map.getSource(target.source)) return
		map.off("styledata", onStyleData)
		apply()
	}
	map.on("styledata", onStyleData)
}

function setTransactionFeatureState(map: mapboxgl.Map, id: number | string, state: Record<string, any>) {
	setFeatureStateWhenSourceReady(
		map,
		{
			source: MapSourceId.Transactions,
			sourceLayer: "tiles.transaction",
			id: id,
		},
		state
	)
}

function setCompletedAssessmentFeatureState(map: mapboxgl.Map, id: number | string, state: Record<string, any>) {
	setFeatureStateWhenSourceReady(
		map,
		{
			source: MapSourceId.CompletedAssessments,
			sourceLayer: "landerz_models.completed_assessments",
			id,
		},
		state
	)
}

function setProspectFeatureStates(map: mapboxgl.Map) {
	for (const prospect of prospects.value) {
		setFeatureStateWhenSourceReady(
			map,
			{
				source: MapSourceId.Prospects,
				sourceLayer: "landerz_models.parcel_prospects",
				id: prospect.prospectID,
			},
			{ potential: prospect.potential }
		)
	}
}

function clearPendingCadastreSelectionRestore(map: mapboxgl.Map): void {
	if (!pendingCadastreSelectionRestore) return
	map.off(MapEventName.SourceData, pendingCadastreSelectionRestore)
	pendingCadastreSelectionRestore = null
}

function restoreSelectedParcelFeatureStates(map: mapboxgl.Map): void {
	clearPendingCadastreSelectionRestore(map)

	const parcelIds = new Set(selectedParcelIDs.value)
	if (selectedParcelID.value != null) {
		parcelIds.add(String(selectedParcelID.value))
	}
	if (parcelIds.size === 0 || !map.getSource(MapSourceId.Cadastre)) return

	const restoreAvailableSelections = (): boolean => {
		const features = querySourceFeatures(map, MapSourceId.Cadastre, {
			sourceLayer: PARCEL_SOURCE_LAYER,
		})
		const restoredParcelIds = new Set<string>()

		for (const feature of features) {
			const parcelId = String(feature.properties?.parcel_id ?? "")
			if (!parcelIds.has(parcelId) || feature.id == null || restoredParcelIds.has(parcelId)) continue

			map.setFeatureState(
				{
					source: MapSourceId.Cadastre,
					sourceLayer: PARCEL_SOURCE_LAYER,
					id: feature.id,
				},
				{ highlight: true }
			)
			restoredParcelIds.add(parcelId)
		}

		return restoredParcelIds.size === parcelIds.size
	}

	if (restoreAvailableSelections()) return

	pendingCadastreSelectionRestore = event => {
		if (event.sourceId !== MapSourceId.Cadastre) return
		if (restoreAvailableSelections()) {
			clearPendingCadastreSelectionRestore(map)
		}
	}
	map.on(MapEventName.SourceData, pendingCadastreSelectionRestore)
}

const handleSearchByAddress = (position: AddressPosition) => {
	const map = mapRef.value
	if (!map) {
		return
	}

	const { lng, lat, bounds, isRegion } = position

	if (isRegion && bounds) {
		map.fitBounds(bounds)
	} else {
		const target = {
			center: [lng, lat] as [number, number],
			zoom: 16,
		}
		isLargeScreen.value ? map.flyTo(target) : map.jumpTo(target)
	}

	// Clear any existing highlight or marker as we could have selected parcel or parcels
	clearHighlights(map)
	clearSelections()
	// Set marker on clicked coordinates.
	//
	// We're doing this concurrently with flyTo/fitBounds so that the
	// marker is already present when the movement nears destination.
	// This adds a sense of targetting and clarity.
	marker.setLngLat({ lng, lat }).addTo(map)

	// highlight parcel under the adress coordinates
	if (!isRegion) {
		map.once("moveend", () => {
			const point = map.project([lng, lat])
			const features = map.queryRenderedFeatures(point, {
				layers: ["cadastre"],
			})
			const feature = features[0]
			if (!feature?.id) {
				return
			}

			map.setFeatureState(
				{
					source: MapSourceId.Cadastre,
					sourceLayer: PARCEL_SOURCE_LAYER,
					id: feature.id,
				},
				{ highlight: true }
			)

			const parcelId = feature.properties?.["parcel_id"]
			if (parcelId) {
				selectedParcels.value = [feature.properties]
				selectedParcelID.value = parcelId
				selectedFeatureId.value = String(feature.id)
				parcelMarkers.value.set(parcelId, marker)
				// open opportunity if an opportunity is linked to this parcel
				const opportunity = storeOpportunities.value.find((opp: Opportunity) =>
					opp.lot_numbers_full?.includes(Number(parcelId))
				)
				if (opportunity) {
					selectedOpportunityId.value = opportunity.id
					emit("opportunity-selected", opportunity)
				}
			}
		})
	}
}

const handleSearchByTransaction = (transaction: Transaction) => {
	store.selectedTransaction = transaction
}

const handleSearchByParcel = (position: ParcelPosition, parcelId: string) => {
	const map = mapRef.value
	if (!map) {
		return
	}
	store.ensureVisibleLayer("cadastre")
	setFeatureDetails([])

	const { lng, lat } = position
	const center = [lng, lat] as [number, number]
	const zoom = 17

	// Clear any existing highlight
	clearHighlights(map)

	// Clear any active selection
	clearSelections()

	// Set marker on clicked coordinates
	//
	// We're doing this concurrently with flyTo so that the marker is
	// already present when the movement nears destination. This adds a
	// sense of targetting and clarity.
	marker.setLngLat({ lng, lat }).addTo(map)
	parcelMarkers.value.set(parcelId, marker)

	const selectParcelFeature = () => {
		const feature = findRenderedCadastreFeature(map, center, parcelId)
		if (feature?.id == null) {
			return false
		}

		map.setFeatureState(
			{
				source: MapSourceId.Cadastre,
				sourceLayer: PARCEL_SOURCE_LAYER,
				id: feature.id,
			},
			{ highlight: true }
		)

		selectedParcels.value = [feature.properties]
		selectedParcelID.value = parcelId
		selectedFeatureId.value = String(feature.id)
		return true
	}

	const selectParcelFeatureWhenCadastreLoads = (event: mapboxgl.MapSourceDataEvent) => {
		if (event.sourceId !== MapSourceId.Cadastre) return
		if (!map.isSourceLoaded(MapSourceId.Cadastre)) return
		if (selectParcelFeature()) {
			map.off(MapEventName.SourceData, selectParcelFeatureWhenCadastreLoads)
		}
	}

	map.once("moveend", () => {
		if (selectParcelFeature()) return
		map.on(MapEventName.SourceData, selectParcelFeatureWhenCadastreLoads)
	})
	map.flyTo({
		center,
		zoom,
		speed: 2,
		curve: 1,
	})
}

// search by opportunity id
const handleSearchByOpportunityId = async (
	opportunityId: string,
	{ navigate = true }: { navigate?: boolean } = {}
): Promise<Opportunity | null> => {
	const map = mapRef.value
	if (!map) return null

	let opportunity: Opportunity | undefined
	try {
		opportunity = (await store.fetchOpportunity(opportunityId)) ?? undefined
	} catch (error) {
		console.error("Error fetching selected opportunity:", error)
	}

	if (!opportunity) {
		if (store.searches.opportunity === opportunityId) store.searches.opportunity = undefined
		emit("error", "search.opportunityNotFound")
		return null
	}

	if (!opportunity.latitude || !opportunity.longitude) {
		if (store.searches.opportunity === opportunityId) store.searches.opportunity = undefined
		emit("error", "search.opportunityNotFound")
		return null
	}

	store.ensureVisibleLayer("opportunities")
	store.ensureLayerSelected(opportunity.type as LayerTypeValue)

	const center = [opportunity.longitude, opportunity.latitude] as [number, number]
	const zoom = 15

	clearHighlights(map)
	clearSelections()
	selectedOpportunityId.value = opportunity.id
	selectedFeatureId.value = opportunity.id

	if (!navigate) {
		emit("opportunity-selected", opportunity)
		return opportunity
	}

	if (isLargeScreen.value) {
		map.flyTo({
			center,
			zoom,
			speed: 2,
			curve: 1,
		})
		map.once("moveend", () => {
			emit("opportunity-selected", opportunity)
			if (store.searches.opportunity === opportunityId) store.searches.opportunity = undefined
		})
	} else {
		map.jumpTo({ center, zoom })
		emit("opportunity-selected", opportunity)
		if (store.searches.opportunity === opportunityId) store.searches.opportunity = undefined
	}

	return opportunity
}

function applyCadastreViewFilter(map: mapboxgl.Map): void {
	if (!map.getLayer("cadastre")) return
	const hasSensitiveAccess = store.currentUser?.has(Permission.ParcelSensitiveRead) ?? false
	const filter = cadastreFilterForView(
		activeParcelView.value.view.value,
		hasSensitiveAccess
	) as mapboxgl.FilterSpecification | null
	map.setFilter("cadastre", filter)
	map.setFilter(ParcelsLayerId.Line, filter)
}

// prettier-ignore
const REGION_COLORS: Record<string, string> = {
	"Côte-Nord":                     "#EDC948",
	"Laval":                         "#EDC948",
	"Gaspésie–Îles-de-la-Madeleine": "#76B7B2",
	"Capitale-Nationale":            "#59A14F",
	"Bas-Saint-Laurent":             "#E15759",
	"Montréal":                      "#E15759",
	"Montérégie":                    "#59A14F",
	"Mauricie":                      "#F28E2B",
	"Laurentides":                   "#B07AA1",
	"Nord-du-Québec":                "#76B7B2",
	"Saguenay–Lac-Saint-Jean":       "#B07AA1",
	"Lanaudière":                    "#4E79A7",
	"Centre-du-Québec":              "#76B7B2",
	"Estrie":                        "#F28E2B",
	"Chaudière-Appalaches":          "#4E79A7",
	"Outaouais":                     "#FF9DA7",
	"Abitibi-Témiscamingue":         "#4E79A7",
}

function regionColorExpression(regionNameProperty: string): mapboxgl.Expression {
	return ["match", ["get", regionNameProperty], ...Object.entries(REGION_COLORS).flat(), "black"]
}

// Administrative Divisions - Regions
function addRegions(
	map: mapboxgl.Map,
	{
		baseUrl,
		sourceName,
		layerName,
		visibleLayers,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_raw.dlayer_admin_regio_s/{z}/{x}/{y}.pbf`],
	})
	const visibility = visibleLayers.includes("administrative-divisions/regions") ? "visible" : "none"
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_raw.dlayer_admin_regio_s",
		type: "fill",
		layout: { visibility },
		paint: {
			"fill-opacity": 0.2,
			"fill-color": regionColorExpression("res_nm_reg"),
		},
		metadata: { custom: true },
	})
	map.addLayer({
		id: `${layerName}_line`,
		source: sourceName,
		"source-layer": "landerz_raw.dlayer_admin_regio_s",
		type: "line",
		layout: { visibility },
		paint: {
			"line-color": regionColorExpression("res_nm_reg"),
			"line-width": 1,
			"line-opacity": 0.6,
		},
		metadata: { custom: true },
	})
}

// Administrative Divisions - Region Labels
function addRegionLabels(
	map: mapboxgl.Map,
	{
		baseUrl,
		sourceName,
		layerName,
		visibleLayers,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_models.administrative_region_labels/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_models.administrative_region_labels",
		type: "symbol",
		layout: {
			"text-field": ["get", "name"],
			"text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
			visibility: visibleLayers.includes("administrative-divisions/regions") ? "visible" : "none",
		},
		paint: {
			"text-color": "#333333",
			"text-halo-color": "#FFFFFF",
			"text-halo-width": 1.5,
		},
		maxzoom: 8,
		metadata: { custom: true },
	})
}

// Administrative Divisions - MRC
function addMRC(
	map: mapboxgl.Map,
	{
		baseUrl,
		sourceName,
		layerName,
		visibleLayers,
		isSatellite,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
		isSatellite: boolean
	}
) {
	const visibility = visibleLayers.includes("administrative-divisions/rcms") ? "visible" : "none"
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_raw.dlayer_admin_mrc_s/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_raw.dlayer_admin_mrc_s",
		type: "fill",
		layout: { visibility },
		paint: {
			"fill-opacity": 0.2,
			"fill-color": regionColorExpression("mrs_nm_reg"),
		},
		metadata: { custom: true },
	})
	map.addLayer({
		id: `${layerName}_line`,
		source: sourceName,
		"source-layer": "landerz_raw.dlayer_admin_mrc_s",
		type: "line",
		layout: { visibility },
		paint: {
			"line-color": isSatellite ? "#FFFFFF" : regionColorExpression("mrs_nm_reg"),
			"line-width": 0.5,
			"line-opacity": isSatellite ? 0.6 : 1,
		},
		metadata: { custom: true },
	})
}

// Administrative Divisions - MRC Labels
function addMRCLabels(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_models.administrative_mrc_labels/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_models.administrative_mrc_labels",
		type: "symbol",
		layout: {
			"text-field": ["get", "name"],
			"text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
			"text-size": ["interpolate", ["linear"], ["zoom"], 6, 9, 10, 13],
			"text-transform": "uppercase",
			"text-allow-overlap": false,
			visibility: visibleLayers.includes("administrative-divisions/rcms") ? "visible" : "none",
		},
		paint: {
			"text-color": "#333333",
			"text-halo-color": "#FFFFFF",
			"text-halo-width": 1.5,
		},
		maxzoom: 12,
	})
}

// prettier-ignore
const METROPOLITAN_AREA_COLOR: mapboxgl.Expression = [
	"match",
	["get", "cms_nm_com"],
	"Communauté métropolitaine de Montréal", "#1dbf73", // var(--primary-green)
	"Communauté métropolitaine de Québec",   "#5B8DEF",
	"black",
]

// Administrative Divisions - Metropolitan Areas
function addMetropolitanAreas(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_raw.dlayer_admin_comet_s/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_raw.dlayer_admin_comet_s",
		type: "fill",
		layout: {
			visibility: visibleLayers.includes("administrative-divisions/metropolitan-areas") ? "visible" : "none",
		},
		paint: {
			"fill-opacity": 0.2,
			"fill-color": METROPOLITAN_AREA_COLOR,
		},
		metadata: { custom: true },
	})
	map.addLayer({
		id: `${layerName}_line`,
		source: sourceName,
		"source-layer": "landerz_raw.dlayer_admin_comet_s",
		type: "line",
		layout: {
			visibility: visibleLayers.includes("administrative-divisions/metropolitan-areas") ? "visible" : "none",
		},
		paint: {
			"line-color": METROPOLITAN_AREA_COLOR,
			"line-width": 1,
			"line-opacity": 0.7,
		},
		metadata: { custom: true },
	})
}

// Administrative Divisions - Municipalities
function addMunicipalities(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
		isSatellite,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
		isSatellite: boolean
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_raw.dlayer_admin_munic_s/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_raw.dlayer_admin_munic_s",
		type: "line",
		layout: {
			visibility: visibleLayers.includes("administrative-divisions/municipalities") ? "visible" : "none",
		},
		paint: {
			"line-color": isSatellite ? "#FFFFFF" : "#5B6B7A",
			"line-width": 1,
			"line-opacity": 0.8,
		},
		minzoom: 9,
		metadata: { custom: true },
	})
}

const MUNICIPALITY_FOCUS_LAYER_ID = "municipality-focus"

function addMunicipalityFocus(map: mapboxgl.Map, sourceName: string) {
	map.addLayer({
		id: MUNICIPALITY_FOCUS_LAYER_ID,
		source: sourceName,
		"source-layer": "landerz_raw.dlayer_admin_munic_s",
		type: "fill",
		layout: { visibility: "none" },
		paint: {
			"fill-color": "#000000",
			"fill-opacity": 0.12,
			"fill-antialias": false,
		},
		minzoom: 8,
	})
}

function applyMunicipalityFocus(map: mapboxgl.Map, municipalityCode: string | null | undefined) {
	if (!map.getLayer(MUNICIPALITY_FOCUS_LAYER_ID)) return

	map.setLayoutProperty(MUNICIPALITY_FOCUS_LAYER_ID, "visibility", municipalityCode ? "visible" : "none")
	if (!municipalityCode) return

	map.setFilter(MUNICIPALITY_FOCUS_LAYER_ID, ["!=", ["get", "mus_co_geo"], municipalityCode])
}

const MUNICIPALITY_LABELS_MIN_ZOOM = 9
const BASE_MAP_SETTLEMENT_LABEL_LAYER_IDS = [
	"place_villages",
	"place_town",
	"place_city_r6",
	"place_city_r5",
	"place_city_dot_r7",
	"place_city_dot_r4",
	"place_city_dot_r2",
	"place_city_dot_z7",
	"place_capital_dot_z7",
	"settlement-minor-label",
	"settlement-major-label",
]
const baseMapSettlementLabelZoomRanges = new Map<string, { minzoom: number; maxzoom: number }>()

function setBaseMapSettlementLabelsYieldToMunicipalities(map: mapboxgl.Map, shouldYield: boolean): void {
	for (const layerId of BASE_MAP_SETTLEMENT_LABEL_LAYER_IDS) {
		const layer = map.getLayer(layerId)
		if (!layer) continue
		if (!baseMapSettlementLabelZoomRanges.has(layerId)) {
			baseMapSettlementLabelZoomRanges.set(layerId, { minzoom: layer.minzoom ?? 0, maxzoom: layer.maxzoom ?? 24 })
		}
		const { minzoom, maxzoom } = baseMapSettlementLabelZoomRanges.get(layerId)!
		const yieldedMaxzoom = Math.max(minzoom, Math.min(maxzoom, MUNICIPALITY_LABELS_MIN_ZOOM))
		map.setLayerZoomRange(layerId, minzoom, shouldYield ? yieldedMaxzoom : maxzoom)
	}
}

// Administrative Divisions - Municipality Labels
function addMunicipalityLabels(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_models.administrative_municipality_labels/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_models.administrative_municipality_labels",
		type: "symbol",
		layout: {
			"text-field": ["get", "name"],
			"text-size": ["interpolate", ["linear"], ["zoom"], 6, 8, 10, 12],
			"text-allow-overlap": false,
			"text-transform": "uppercase",
			"symbol-placement": "point",
			visibility: visibleLayers.includes("administrative-divisions/municipalities") ? "visible" : "none",
		},
		paint: {
			"text-color": "#3E4A56",
			"text-halo-color": "#FFFFFF",
			"text-halo-width": 2,
		},
		minzoom: MUNICIPALITY_LABELS_MIN_ZOOM,
		metadata: { custom: true },
	})
}

// prettier-ignore
const BOROUGH_COLOR: mapboxgl.Expression = [
	"match",
	["get", "ars_nm_arr"],
	Borough.AhuntsicCartierville,                "#C77DDB",
	Borough.Anjou,                               "#C77DDB",
	Borough.Arrondissement1,                     "#4B3FA8",
	Borough.Arrondissement2,                     "#C77DDB",
	Borough.Arrondissement3,                     "#7A1FA2",
	Borough.Arrondissement4,                     "#5A1846",
	Borough.Beauport,                            "#E58BC9",
	Borough.Calumet,                             "#B5179E",
	Borough.Charlesbourg,                        "#5A1846",
	Borough.Chicoutimi,                          "#4B3FA8",
	Borough.CoteDesNeigesNotreDameDeGrace,       "#5A1846",
	Borough.Desjardins,                          "#4B3FA8",
	Borough.GreenfieldPark,                      "#E58BC9",
	Borough.Grenville,                           "#9C8FE0",
	Borough.Jonquiere,                           "#C77DDB",
	Borough.LIleBizardSainteGenevieve,           "#E58BC9",
	Borough.LaBaie,                              "#7A1FA2",
	Borough.LaCiteLimoilou,                      "#4B3FA8",
	Borough.LaHauteSaintCharles,                 "#7A1FA2",
	Borough.LaSalle,                             "#7A1FA2",
	Borough.Lachine,                             "#6C7BD9",
	Borough.LePlateauMontRoyal,                  "#6C7BD9",
	Borough.LeSudOuest,                          "#4B3FA8",
	Borough.LeVieuxLongueuil,                    "#B5179E",
	Borough.LesChutesDeLaChaudiereEst,           "#7A1FA2",
	Borough.LesChutesDeLaChaudiereOuest,         "#C77DDB",
	Borough.LesRivieres,                         "#C77DDB",
	Borough.MacNider,                            "#5A1846",
	Borough.MercierHochelagaMaisonneuve,         "#E58BC9",
	Borough.MontrealNord,                        "#B5179E",
	Borough.Outremont,                           "#9C8FE0",
	Borough.PierrefondsRoxboro,                  "#4B3FA8",
	Borough.RivieredesPrairiesPointeAuxTrembles, "#5A1846",
	Borough.RosemontLaPetitePatrie,              "#7A1FA2",
	Borough.SaintHubert,                         "#9C8FE0",
	Borough.SaintLaurent,                        "#7A1FA2",
	Borough.SaintLeonard,                        "#9C8FE0",
	Borough.SainteFoySilleryCapRouge,            "#B5179E",
	Borough.Verdun,                              "#E58BC9",
	Borough.VilleMarie,                          "#B5179E",
	Borough.VilleraySaintMichelParcExtension,    "#4B3FA8",
	"black",
]

// Administrative Divisions - Boroughs
function addBorougs(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	const visibility = visibleLayers.includes("administrative-divisions/boroughs") ? "visible" : "none"
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_raw.dlayer_admin_arron_s/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_raw.dlayer_admin_arron_s",
		type: "fill",
		layout: { visibility },
		paint: {
			"fill-opacity": 0.2,
			"fill-color": BOROUGH_COLOR,
		},
		metadata: { custom: true },
	})
	map.addLayer({
		id: `${layerName}_line`,
		source: sourceName,
		"source-layer": "landerz_raw.dlayer_admin_arron_s",
		type: "line",
		layout: { visibility },
		paint: {
			"line-color": BOROUGH_COLOR,
			"line-width": 0.5,
			"line-opacity": 0.4,
		},
		metadata: { custom: true },
	})
}

// Postal Codes
function addPostalCodes(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_raw.dlayer_postal_codes_localdeliveryunitsregion/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_raw.dlayer_postal_codes_localdeliveryunitsregion",
		type: "line",
		layout: {
			visibility: visibleLayers.includes("administrative-divisions/postal-codes") ? "visible" : "none",
		},
		paint: {
			"line-color": "#E8827D",
			"line-opacity": 0.75,
		},
		minzoom: 13, // same as postal-code_labels
		metadata: { custom: true },
	})
}

// Postal Code Labels
function addPostalCodeLabels(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_models.postalcode_labels/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_models.postalcode_labels",
		type: "symbol",
		layout: {
			"text-field": ["get", "postalcode"],
			"text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
			"text-size": ["interpolate", ["linear"], ["zoom"], 13, 9, 16, 12],
			"text-allow-overlap": false,
			visibility: visibleLayers.includes("administrative-divisions/postal-codes") ? "visible" : "none",
		},
		paint: {
			"text-color": "#333333",
			"text-halo-color": "#FFFFFF",
			"text-halo-width": 1.5,
		},
		minzoom: 13, // same as postal-codes
		metadata: { custom: true },
	})
}

// Urban Planning
function addUrbanPlanning(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_models.urban_plan_polygons/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_models.urban_plan_polygons",
		type: "fill",
		layout: {
			visibility: visibleLayers.includes("urban-planning") ? "visible" : "none",
		},
		paint: {
			"fill-opacity": 0.38,
			// prettier-ignore
			"fill-color": [
				"match",
				["get", "affectation"],
				"aeroportuaire",      "#6B7A99",
				"industrielle",       "#8B5FBF",
				"villegiature",       "#6EC6E8",
				"urbaine",            "#979DC4",
				"multifonctionnelle", "#E8935B",
				"recreative",         "#8FD14F",
				"rurale",             "#D19D61",
				"extraction",         "#8C5A3C",
				"forestiere",         "#309F4D",
				"commerciale",        "#E05252",
				"conservation",       "#42C9AC",
				"agricole",           "#A8C93F",
				"residentielle",      "#F0C93B",
				"utilité",            "#7C93A8",
				"agro-forestiere",    "#7A8C4E",
				"rgba(0, 0, 0, 0)",
			],
		},
		metadata: { custom: true },
	})
}

function addUrbanPerimeters(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
		beforeLayerName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
		beforeLayerName?: string
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/core_public.urban_perimeter/{z}/{x}/{y}.pbf`],
	})

	map.addLayer(
		{
			id: layerName,
			source: sourceName,
			"source-layer": "core_public.urban_perimeter",
			type: "fill",
			layout: {
				visibility: visibleLayers.includes("urban-perimeters") ? "visible" : "none",
			},
			paint: {
				"fill-color": "#f76f14",
				"fill-opacity": 0.4,
			},
			metadata: { custom: true },
		},
		beforeLayerName
	)
}

function addCityPvDocuments(map: mapboxgl.Map, baseUrl: string, visibleLayers: string[]) {
	const visibility = visibleLayers.includes("city-pv-documents") ? "visible" : "none"

	map.addSource(CITY_PV_DOCUMENTS_SOURCE_NAME, {
		type: "vector",
		tiles: [`${baseUrl}/${CITY_PV_DOCUMENTS_SOURCE_LAYER}/{z}/{x}/{y}.pbf`],
		promoteId: "city_pv_document_id",
	})

	map.addLayer({
		id: "city-pv-documents-halo",
		source: CITY_PV_DOCUMENTS_SOURCE_NAME,
		"source-layer": CITY_PV_DOCUMENTS_SOURCE_LAYER,
		type: "circle",
		layout: { visibility },
		paint: {
			"circle-color": CITY_PV_DOCUMENTS_COLOR,
			"circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 5, 10, 7, 14, 10, 18, 16, 22, 24],
			"circle-opacity": 0.18,
			"circle-blur": 0.55,
		},
		metadata: { custom: true },
	})

	map.addLayer({
		id: "city-pv-documents",
		source: CITY_PV_DOCUMENTS_SOURCE_NAME,
		"source-layer": CITY_PV_DOCUMENTS_SOURCE_LAYER,
		type: "circle",
		layout: { visibility },
		paint: {
			"circle-color": CITY_PV_DOCUMENTS_COLOR,
			"circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 2.5, 10, 3.5, 14, 5.5, 18, 9, 22, 14],
			"circle-opacity": 0.92,
			"circle-stroke-color": "#ffffff",
			"circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 4, 0.75, 14, 1.5, 18, 2.25, 22, 3],
			"circle-stroke-opacity": 0.95,
		},
		metadata: { custom: true },
	})
}

const cityPvDocumentPropertyOrder: Array<[property: string, label: string]> = [
	["name_of_project_resolution_short", "name"],
	["date", "date"],
	["url_drive", "document"],
	["lot_number", "lot-number"],
	["address", "address"],
	["resolution_category", "resolution-category"],
	["type_of_resolution", "resolution-type"],
	["keyword_classes", "keyword-classes"],
	["full_minutes", "full-minutes"],
	["resolution_ou_dossier", "resolution-or-file"],
	["city_pv_document_id", "document-id"],
	["file_id", "file-id"],
	["segment_id", "segment-id"],
]

function buildCityPvDocumentDetail(feature: mapboxgl.MapboxGeoJSONFeature): LayerFeatureDetail {
	const props: Record<string, unknown> = feature.properties ?? {}
	const rows: LayerFeatureSpec[] = []

	for (const [property, label] of cityPvDocumentPropertyOrder) {
		if (!(property in props)) continue
		const value = props[property]
		rows.push({
			label,
			value: value == null ? null : String(value),
			isLink: property === "url_drive",
		})
	}

	const documentId = props.city_pv_document_id ?? feature.id ?? props.segment_id ?? Math.random()
	return {
		id: `city-pv-document-${documentId}`,
		type: LayerFeatureType.CityPvDocument,
		rows,
	}
}

function pushIfPresent(rows: LayerFeatureSpec[], label: string, value: unknown) {
	if (value == null) return
	const normalized = String(value)
	if (normalized === "" || normalized === "None" || normalized === "null") return
	rows.push({ label, value: normalized })
}

function buildPlanningPolicyDetail(feature: mapboxgl.MapboxGeoJSONFeature): LayerFeatureDetail | null {
	const props: any = feature.properties ?? {}
	const type: string | undefined = props.type
	let mappedType: LayerFeatureType | null = null

	switch (type) {
		case "TOD":
			mappedType = LayerFeatureType.Tod
			break
		case "PPU":
			mappedType = LayerFeatureType.Ppu
			break
		case "Corridor de transport métropolitain":
			mappedType = LayerFeatureType.Corridor
			break
		case "Secteur de planification stratégique":
		case "SPI":
			mappedType = LayerFeatureType.Strategic
			break
		default:
			mappedType = null
	}

	if (!mappedType) return null

	const rows: LayerFeatureSpec[] = []
	if (props.source) rows.push({ label: "source", value: String(props.source) })
	if (props.name) rows.push({ label: "name", value: String(props.name) })
	if (mappedType === LayerFeatureType.Tod && props.transit_type)
		rows.push({ label: "transit-type", value: String(formatCommaList(props.transit_type)) })
	if (mappedType === LayerFeatureType.Tod && props.transit_line)
		rows.push({ label: "transit-line", value: String(formatCommaList(props.transit_line)) })
	if (type === "Secteur de planification stratégique" || type === "SPI") {
		if (props.type) rows.push({ label: "type", value: String(props.type) })
	}
	if (props.version != null) {
		rows.push({ label: "version", value: String(props.version) })
	}
	if (props.min_units_ha != null) {
		rows.push({ label: "min-units-ha", value: String(props.min_units_ha) })
	}
	const doc = props.url_doc?.toString()
	if (doc && doc !== "None") rows.push({ label: "document", value: doc, isLink: true })
	const mapLink = props.url_map?.toString()
	if (mapLink && mapLink !== "None") rows.push({ label: "map", value: mapLink, isLink: true })

	return { id: String(feature.id ?? `${props.name ?? props.type ?? Math.random()}`), type: mappedType, rows }
}

function buildSoilDetail(feature: mapboxgl.MapboxGeoJSONFeature): LayerFeatureDetail {
	const soilClass = feature.properties?.soil_class ?? null
	return {
		id: `soil-${feature.id ?? Math.random()}`,
		type: LayerFeatureType.Soil,
		rows: [
			{ label: "soil-class", value: soilClass ? String(soilClass) : "—" },
			{ label: "document", value: "https://carto.cptaq.gouv.qc.ca/doc/ARDA.pdf", isLink: true },
		],
	}
}

function buildEndangeredSpeciesDetail(feature: mapboxgl.MapboxGeoJSONFeature): LayerFeatureDetail {
	const props: any = feature.properties ?? {}
	const rows: LayerFeatureSpec[] = []
	pushIfPresent(rows, "name-fr", props.name_fr)
	pushIfPresent(rows, "name-en", props.name_en)
	pushIfPresent(rows, "species-type", props.species_type)
	pushIfPresent(rows, "species-vulnerability-class", props.species_vulnerability_class)
	pushIfPresent(rows, "last-observation-date", props.last_observation_date)
	const link = props.url?.toString()
	if (link && link !== "None") rows.push({ label: "url", value: link, isLink: true })

	return {
		id: `endangered-species-${feature.id ?? `${props.name_fr ?? props.name_en ?? props.species_type ?? Math.random()}`}`,
		type: LayerFeatureType.EndangeredSpecies,
		rows,
	}
}

function capitalizeFirst(value: unknown): unknown {
	if (typeof value !== "string" || value.length === 0) return value
	return value.charAt(0).toLocaleUpperCase(currentLocale.value) + value.slice(1)
}

// Postgres text[] columns come through the tile source as their array literal, e.g. "{agriculture,habitation}"
function formatPgTextArray(value: unknown): unknown {
	if (typeof value !== "string" || !value.startsWith("{") || !value.endsWith("}")) return value
	return formatCommaList(value.slice(1, -1))
}

// some tile properties are comma-joined without braces, e.g. "Métro,REM,Train (EXO)"
function formatCommaList(value: unknown): unknown {
	if (typeof value !== "string") return value
	return value
		.split(",")
		.map(item => item.trim())
		.filter(Boolean)
		.map(item => capitalizeFirst(item))
		.join(", ")
}

function buildZoningDetail(feature: mapboxgl.MapboxGeoJSONFeature): LayerFeatureDetail {
	const props: any = feature.properties ?? {}
	const rows: LayerFeatureSpec[] = []
	pushIfPresent(rows, "zone-code", props.zone_code)
	pushIfPresent(rows, "city", capitalizeFirst(props.city))
	pushIfPresent(rows, "borough", props.arrondissement)
	pushIfPresent(rows, "use-group", formatPgTextArray(props.use_group_canonical))
	const gridUrl = props.url_document?.toString()
	if (gridUrl && gridUrl !== "None") rows.push({ label: "zoning-grid", value: gridUrl, isLink: true })

	return {
		id: `zoning-${feature.id ?? `${props.zone_code ?? props.city ?? Math.random()}`}`,
		type: LayerFeatureType.Zoning,
		rows,
	}
}

function getActiveEndangeredSpeciesLayerIds(): string[] {
	return endangeredSpeciesLayerDefinitions.flatMap(definition =>
		filters.value.endangeredSpecies[definition.filterKey] ? [definition.id, `${definition.id}-plants`] : []
	)
}

function buildTransitLineDetail(feature: mapboxgl.MapboxGeoJSONFeature): LayerFeatureDetail {
	const props: any = feature.properties ?? {}
	const rows: LayerFeatureSpec[] = []
	pushIfPresent(rows, "name", props.name)
	pushIfPresent(rows, "mode", props.mode)
	pushIfPresent(rows, "status", props.status)
	pushIfPresent(rows, "year", props.year)

	return {
		id: `transit-line-${feature.id ?? `${props.name ?? Math.random()}`}`,
		type: LayerFeatureType.TransitLine,
		rows,
	}
}

function buildTransitStationDetail(feature: mapboxgl.MapboxGeoJSONFeature): LayerFeatureDetail {
	const props: any = feature.properties ?? {}
	const rows: LayerFeatureSpec[] = []
	pushIfPresent(rows, "name", props.name)
	pushIfPresent(rows, "mode", props.mode)
	pushIfPresent(rows, "line", props.line ?? props.transit_line)
	pushIfPresent(rows, "status", props.status)

	return {
		id: `transit-station-${feature.id ?? `${props.name ?? Math.random()}`}`,
		type: LayerFeatureType.TransitStation,
		rows,
	}
}

function completedAssessmentValue(value: unknown): string {
	if (value == null || value === "") {
		return "—"
	}

	return String(formatPgTextArray(value))
}

function buildCompletedAssessmentDetail(feature: mapboxgl.MapboxGeoJSONFeature): LayerFeatureDetail {
	const props = feature.properties ?? {}
	const rows: LayerFeatureSpec[] = [
		{ label: "transaction-name", value: completedAssessmentValue(props.transaction_name) },
		{ label: "address", value: completedAssessmentValue(props.address) },
		{ label: "city", value: completedAssessmentValue(props.city) },
		{ label: "owner", value: completedAssessmentValue(props.owner) },
		{ label: "lots", value: completedAssessmentValue(props.lots) },
		props.hubspot_link
			? { label: "hubspot-link", value: String(props.hubspot_link), isLink: true }
			: { label: "hubspot-link", value: "—" },
		props.data_room_link
			? { label: "data-room-link", value: String(props.data_room_link), isLink: true }
			: { label: "data-room-link", value: "—" },
	]

	return {
		id: `completed-assessment-${feature.id ?? props.hubspot_id ?? Math.random()}`,
		type: LayerFeatureType.CompletedAssessment,
		rows,
	}
}

function collectIntersectDetails(map: mapboxgl.Map, point: mapboxgl.PointLike): LayerFeatureDetail[] {
	const layers: string[] = []
	const soilOn = isLayerVisible(map, "soil-capability")
	const planningOn = isLayerVisible(map, "planning-policy")
	const transitLinesOn = isLayerVisible(map, "transit-lines")
	const transitStationsOn = isLayerVisible(map, "transit-stations")
	const endangeredSpeciesOn = isLayerVisible(map, "endangered-species")
	const zoningOn = isLayerVisible(map, "zoning")
	const cityPvDocumentsOn = isLayerVisible(map, "city-pv-documents")

	if (soilOn) {
		layers.push("soil-capability")
	}
	if (planningOn) {
		layers.push("planning-policy-hit")
	}
	if (transitLinesOn) {
		layers.push("transit-lines")
	}
	if (transitStationsOn) {
		layers.push("transit-stations")
		layers.push("transit-stations_labels")
	}
	if (endangeredSpeciesOn) {
		layers.push(...getActiveEndangeredSpeciesLayerIds())
	}
	if (zoningOn) {
		layers.push("zoning")
	}
	if (cityPvDocumentsOn) {
		layers.push("city-pv-documents")
	}

	if (!layers.length) return []

	const features = map.queryRenderedFeatures(point, { layers })
	const seen = new Set<string>()
	const details: LayerFeatureDetail[] = []

	for (const f of features) {
		if (!f.layer?.id) continue
		const isTransitStationFeature = f.layer.id === "transit-stations" || f.layer.id === "transit-stations_labels"
		let key = `${f.properties?.name ?? ""}|${f.properties?.type ?? ""}|${f.properties?.source ?? ""}|${f.layer?.id ?? ""}|${f.id ?? ""}`
		if (isTransitStationFeature) {
			key = `transit-station|${f.id ?? ""}|${f.properties?.name ?? ""}|${f.properties?.line ?? f.properties?.transit_line ?? ""}`
		} else if (f.layer.id.startsWith("endangered-species")) {
			key = `endangered-species|${f.id ?? ""}|${f.properties?.name_fr ?? ""}|${f.properties?.name_en ?? ""}|${f.properties?.species_type ?? ""}|${f.properties?.species_vulnerability_class ?? ""}|${f.properties?.url ?? ""}`
		} else if (f.layer.id === "zoning") {
			key = `zoning|${f.id ?? ""}|${f.properties?.zone_code ?? ""}|${f.properties?.city ?? ""}`
		} else if (f.layer.id === "city-pv-documents") {
			key = `city-pv-document|${f.properties?.city_pv_document_id ?? f.id ?? f.properties?.segment_id ?? ""}`
		}
		if (seen.has(key)) continue
		seen.add(key)

		if (f.layer.id.startsWith("soil-capability")) {
			details.push(buildSoilDetail(f))
			continue
		}

		if (f.layer.id.startsWith("planning-policy")) {
			const detail = buildPlanningPolicyDetail(f)
			if (detail) details.push(detail)
			continue
		}

		if (f.layer.id.startsWith("endangered-species")) {
			const detail = buildEndangeredSpeciesDetail(f)
			if (detail.rows.length > 0) details.push(detail)
			continue
		}

		if (f.layer.id === "transit-lines") {
			const detail = buildTransitLineDetail(f)
			if (detail.rows.length > 0) details.push(detail)
			continue
		}

		if (f.layer.id === "transit-stations" || f.layer.id === "transit-stations_labels") {
			const detail = buildTransitStationDetail(f)
			if (detail.rows.length > 0) details.push(detail)
			continue
		}

		if (f.layer.id === "zoning") {
			const detail = buildZoningDetail(f)
			if (detail.rows.length > 0) details.push(detail)
			continue
		}

		if (f.layer.id === "city-pv-documents") {
			const detail = buildCityPvDocumentDetail(f)
			if (detail.rows.length > 0) details.push(detail)
		}
	}

	return details
}

// Endangered Species
// Land Use
function addLandUse(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_models.dlayer_land_use/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_models.dlayer_land_use",
		type: "fill",
		layout: {
			visibility: visibleLayers.includes("land-use") ? "visible" : "none",
		},
		paint: {
			"fill-opacity": 0.2,
			"fill-color": "#C74B87",
		},
		metadata: { custom: true },
	})
}

// Agricultural Inclusions/Exclusions
function addAgriculturalInclusionsExclusions(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/core_public.agricultural_inclusions_exclusions/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "core_public.agricultural_inclusions_exclusions",
		type: "fill",
		layout: {
			visibility: visibleLayers.includes("agricultural-inclusions-exclusions") ? "visible" : "none",
		},
		paint: {
			"fill-opacity": 0.5,
			"fill-color": ["match", ["get", "resultat"], "EXCLUSION", "#e53935", "INCLUSION", "#43a047", "#cccccc"],
		},
		minzoom: 11,
		metadata: { custom: true },
	})
}

// Prospects
function getProspectPotentialExpression(): mapboxgl.Expression {
	return ["coalesce", ["feature-state", "potential"], ["get", "potential"]] as mapboxgl.Expression
}

function getProspectColorExpression(): mapboxgl.Expression {
	return [
		"match",
		getProspectPotentialExpression(),
		"High",
		"#B76165",
		"Medium",
		"#F3B41E",
		"Low",
		"#3A7EAB",
		"None",
		"#333333",
		"Unspecified",
		"transparent",
		"black",
	] as mapboxgl.Expression
}

function getProspectOpacityExpression(filters: Filters): mapboxgl.Expression {
	const expr: any[] = ["case", ["boolean", ["feature-state", "transparent"], false], 0]

	if (filters.developmentPotential.high) {
		expr.push(["==", getProspectPotentialExpression(), "High"], 0.5)
	}

	if (filters.developmentPotential.medium) {
		expr.push(["==", getProspectPotentialExpression(), "Medium"], 0.5)
	}

	if (filters.developmentPotential.low) {
		expr.push(["==", getProspectPotentialExpression(), "Low"], 0.5)
	}

	expr.push(0)

	return expr as mapboxgl.Expression
}

function addProspects(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_models.parcel_prospects/{z}/{x}/{y}.pbf`],
		promoteId: "parcel_id",
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_models.parcel_prospects",
		type: "fill",
		layout: {
			visibility: visibleLayers.includes("prospects") ? "visible" : "none",
		},
		paint: {
			"fill-opacity": getProspectOpacityExpression(filters.value),
			"fill-color": getProspectColorExpression(),
		},
		minzoom: 13,
		metadata: { custom: true },
	})
}

// Urban Priorities
// initial setup
function addUrbanPriorities(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_models.parcel_urban_priority/{z}/{x}/{y}.pbf`],
	})

	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_models.parcel_urban_priority",
		type: "fill",
		layout: {
			visibility: visibleLayers.includes("urban-priorities") ? "visible" : "none",
		},
		paint: {
			"fill-color": [
				"case",
				["==", ["get", "pmad_active"], true],
				"#1A0F5C",
				["==", ["get", "pmad_draft"], true],
				"#1A0F5C",

				["==", ["get", "sad_active"], true],
				"#3B2DBF",
				["==", ["get", "sad_draft"], true],
				"#3B2DBF",

				["==", ["get", "pu_active"], true],
				"#7A4FE0",
				["==", ["get", "pu_draft"], true],
				"#7A4FE0",

				["==", ["get", "ppu_active"], true],
				"#B84FC9",
				["==", ["get", "ppu_draft"], true],
				"#B84FC9",

				["==", ["get", "pu_active_interview"], true],
				"#E89BD9",
				["==", ["get", "pu_draft_interview"], true],
				"#E89BD9",

				["==", ["get", "other_active"], true],
				"#858198",
				["==", ["get", "other_draft"], true],
				"#858198",

				"white", // fallback
			],

			"fill-opacity": [
				"case",
				["boolean", ["feature-state", "transparent"], false],
				0,
				// Active = high opacity
				["==", ["get", "pmad_active"], true],
				0.75,
				["==", ["get", "sad_active"], true],
				0.75,
				["==", ["get", "pu_active"], true],
				0.75,
				["==", ["get", "ppu_active"], true],
				0.75,
				["==", ["get", "pu_active_interview"], true],
				0.75,
				["==", ["get", "other_active"], true],
				0.75,

				// Draft = low opacity
				["==", ["get", "pmad_draft"], true],
				0.25,
				["==", ["get", "sad_draft"], true],
				0.25,
				["==", ["get", "pu_draft"], true],
				0.25,
				["==", ["get", "ppu_draft"], true],
				0.25,
				["==", ["get", "pu_draft_interview"], true],
				0.25,
				["==", ["get", "other_draft"], true],
				0.25,

				0, // fallback
			],
		},

		minzoom: 13,
		metadata: { custom: true },
	})
}

// interactive change when urban priorities are toggled
function getUrbanPriorityColorExpression(filters: Filters): mapboxgl.Expression {
	const expr: mapboxgl.Expression = ["case"]

	// PMAD
	if (filters.urbanPriorities.pmad.active) {
		expr.push(["==", ["get", "pmad_active"], true], "#1A0F5C")
	}
	if (filters.urbanPriorities.pmad.draft) {
		expr.push(["==", ["get", "pmad_draft"], true], "#1A0F5C")
	}

	// SAD
	if (filters.urbanPriorities.sad.active) {
		expr.push(["==", ["get", "sad_active"], true], "#3B2DBF")
	}
	if (filters.urbanPriorities.sad.draft) {
		expr.push(["==", ["get", "sad_draft"], true], "#3B2DBF")
	}

	// PU
	if (filters.urbanPriorities.pu.active) {
		expr.push(["==", ["get", "pu_active"], true], "#7A4FE0")
	}
	if (filters.urbanPriorities.pu.draft) {
		expr.push(["==", ["get", "pu_draft"], true], "#7A4FE0")
	}

	// PPU
	if (filters.urbanPriorities.ppu.active) {
		expr.push(["==", ["get", "ppu_active"], true], "#B84FC9")
	}
	if (filters.urbanPriorities.ppu.draft) {
		expr.push(["==", ["get", "ppu_draft"], true], "#B84FC9")
	}

	// PU Interview
	if (filters.urbanPriorities.pu_interview.active) {
		expr.push(["==", ["get", "pu_active_interview"], true], "#E89BD9")
	}
	if (filters.urbanPriorities.pu_interview.draft) {
		expr.push(["==", ["get", "pu_draft_interview"], true], "#E89BD9")
	}

	// Other
	if (filters.urbanPriorities.other.active) {
		expr.push(["==", ["get", "other_active"], true], "#858198")
	}
	if (filters.urbanPriorities.other.draft) {
		expr.push(["==", ["get", "other_draft"], true], "#858198")
	}

	expr.push("transparent")
	return expr as mapboxgl.Expression
}

function getUrbanPriorityOpacityExpression(filters: Filters): mapboxgl.Expression {
	const expr: any[] = ["case"]

	// transparent feature-state
	expr.push(["boolean", ["feature-state", "transparent"], false], 0)

	// PMAD
	if (filters.urbanPriorities.pmad.active) {
		expr.push(["==", ["get", "pmad_active"], true], 0.75)
	}
	if (filters.urbanPriorities.pmad.draft) {
		expr.push(["==", ["get", "pmad_draft"], true], 0.25)
	}

	// SAD
	if (filters.urbanPriorities.sad.active) {
		expr.push(["==", ["get", "sad_active"], true], 0.75)
	}
	if (filters.urbanPriorities.sad.draft) {
		expr.push(["==", ["get", "sad_draft"], true], 0.25)
	}

	// PU
	if (filters.urbanPriorities.pu.active) {
		expr.push(["==", ["get", "pu_active"], true], 0.75)
	}
	if (filters.urbanPriorities.pu.draft) {
		expr.push(["==", ["get", "pu_draft"], true], 0.25)
	}

	// PPU
	if (filters.urbanPriorities.ppu.active) {
		expr.push(["==", ["get", "ppu_active"], true], 0.75)
	}
	if (filters.urbanPriorities.ppu.draft) {
		expr.push(["==", ["get", "ppu_draft"], true], 0.25)
	}

	// PU Interview
	if (filters.urbanPriorities.pu_interview.active) {
		expr.push(["==", ["get", "pu_active_interview"], true], 0.75)
	}
	if (filters.urbanPriorities.pu_interview.draft) {
		expr.push(["==", ["get", "pu_draft_interview"], true], 0.25)
	}

	// Other
	if (filters.urbanPriorities.other.active) {
		expr.push(["==", ["get", "other_active"], true], 0.75)
	}
	if (filters.urbanPriorities.other.draft) {
		expr.push(["==", ["get", "other_draft"], true], 0.25)
	}

	expr.push(0)
	return expr as mapboxgl.Expression
}

function loadImageSrc(src: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const image = new Image()
		image.onload = () => resolve(image)
		image.onerror = () => reject(new Error("Failed to load generated icon SVG"))
		image.src = src
	})
}

async function rasterizeImageSrc(src: string, pixelRatio: number): Promise<ImageData> {
	const image = await loadImageSrc(src)
	const canvas = document.createElement("canvas")
	canvas.width = image.width * pixelRatio
	canvas.height = image.height * pixelRatio
	const ctx = canvas.getContext("2d")
	if (!ctx) {
		throw new Error("Canvas 2D context unavailable")
	}
	ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
	return ctx.getImageData(0, 0, canvas.width, canvas.height)
}

function rasterizeIconSvg(svg: string, pixelRatio: number): Promise<ImageData> {
	return rasterizeImageSrc(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`, pixelRatio)
}

async function loadOpportunityIcons(map: mapboxgl.Map, isCurrentStyle: () => boolean): Promise<void> {
	const pixelRatio = Math.max(2, Math.ceil(window.devicePixelRatio * 2))
	await Promise.all(
		Object.values(OpportunityType).map(async type => {
			try {
				const icon = resolveIconFamily(type)
				if (!icon || map.hasImage(icon.family)) return
				const imageData = await rasterizeIconSvg(
					buildOpportunityIconSvg(icon.color, [], { symmetric: true }),
					pixelRatio
				)
				if (!isCurrentStyle()) return
				if (!map.hasImage(icon.family)) map.addImage(icon.family, imageData, { pixelRatio })
			} catch (error) {
				console.warn("Failed to load opportunity icon", { type, error })
			}
		})
	)

	await Promise.all(
		[
			{ id: OPPORTUNITY_NEW_BADGE_IMAGE_ID, badge: IconBadge.New },
			{ id: OPPORTUNITY_OFFER_BADGE_IMAGE_ID, badge: IconBadge.Offer },
			{ id: OPPORTUNITY_LIKED_BADGE_IMAGE_ID, badge: IconBadge.Liked },
			{ id: OPPORTUNITY_DISLIKED_BADGE_IMAGE_ID, badge: IconBadge.Discarded },
		].map(async ({ id, badge }) => {
			try {
				if (map.hasImage(id)) return
				const image = await loadImageSrc(buildBadgeIconDataUri(badge))
				if (!isCurrentStyle()) return
				if (!map.hasImage(id)) map.addImage(id, image)
			} catch (error) {
				console.warn("Failed to load opportunity badge icon", { id, badge, error })
			}
		})
	)
}

const viewportOpportunityCache = new Map<string, Opportunity | null>()
let viewportOpportunityRequestId = 0
let pendingViewportOpportunityIdsKey: string | null = null
let lastViewportOpportunityIdsKey: string | null = null

function getOpportunityIdsInViewport(): string[] {
	const map = mapRef.value
	if (!map || !map.getLayer("opportunities-icons")) return []

	// Query rendered icons rather than lng/lat bounds: getBounds() over-includes when the map is rotated/pitched.
	const renderedIds = new Set<string>()
	for (const feature of map.queryRenderedFeatures(undefined as any, { layers: [OPPORTUNITY_LAYER_ID] })) {
		if (isPendingOpportunityTileFeature(feature)) continue
		const id = feature.properties?.id ?? feature.id
		if (id != null) renderedIds.add(String(id))
	}
	return [...renderedIds]
}

function getOpportunityCountInViewport(): number {
	const map = mapRef.value
	if (!map) return 0

	const renderedOpportunityIds = new Set<string>()
	if (map.getLayer(OPPORTUNITY_LAYER_ID)) {
		for (const feature of map.queryRenderedFeatures(undefined as any, { layers: [OPPORTUNITY_LAYER_ID] })) {
			const id = feature.properties?.id ?? feature.id
			if (id != null) renderedOpportunityIds.add(String(id))
		}
	}

	const renderedClusterIds = new Set<string>()
	let clusteredOpportunityCount = 0
	if (map.getLayer(OPPORTUNITY_CLUSTER_LAYER_ID)) {
		for (const feature of map.queryRenderedFeatures(undefined as any, {
			layers: [OPPORTUNITY_CLUSTER_LAYER_ID],
		})) {
			const id = feature.properties?.id ?? feature.id
			if (id == null || renderedClusterIds.has(String(id))) continue

			const pointCount = Number(feature.properties?.point_count)
			if (!Number.isFinite(pointCount)) continue

			renderedClusterIds.add(String(id))
			clusteredOpportunityCount += pointCount
		}
	}

	return renderedOpportunityIds.size + clusteredOpportunityCount
}

function getOpportunitiesInViewport(): Opportunity[] {
	const renderedIds = getOpportunityIdsInViewport()
	const opportunitiesById = new Map(
		(storeOpportunities.value as Opportunity[]).map(opportunity => [String(opportunity.id), opportunity])
	)
	for (const [id, opportunity] of viewportOpportunityCache) {
		if (opportunity) opportunitiesById.set(id, opportunity)
	}
	return [...renderedIds].map(id => opportunitiesById.get(id)).filter((o): o is Opportunity => o != null)
}

async function emitViewportOpportunities(): Promise<void> {
	emit("viewport-opportunity-count-changed", getOpportunityCountInViewport())

	const renderedIds = getOpportunityIdsInViewport()
	const idsKey = [...renderedIds].sort().join(",")
	if (idsKey === lastViewportOpportunityIdsKey || idsKey === pendingViewportOpportunityIdsKey) return

	const requestId = ++viewportOpportunityRequestId
	pendingViewportOpportunityIdsKey = idsKey
	const storeOpportunityIds = new Set(
		(storeOpportunities.value as Opportunity[]).map(opportunity => String(opportunity.id))
	)
	const missingIds = renderedIds.filter(id => !storeOpportunityIds.has(id) && !viewportOpportunityCache.has(id))

	try {
		if (missingIds.length > 0) {
			const opportunities = await store.fetchOpportunitiesByIds(missingIds)
			if (requestId !== viewportOpportunityRequestId) return

			for (const id of missingIds) viewportOpportunityCache.set(id, null)
			for (const opportunity of opportunities) {
				viewportOpportunityCache.set(String(opportunity.id), opportunity)
			}
		}

		if (requestId !== viewportOpportunityRequestId) return
		lastViewportOpportunityIdsKey = idsKey
		emit("viewport-opportunities-changed", getOpportunitiesInViewport())
	} catch (error) {
		console.error("Failed to load opportunities in the map viewport:", error)
	} finally {
		if (requestId === viewportOpportunityRequestId) pendingViewportOpportunityIdsKey = null
	}
}

watch(
	storeOpportunities,
	() => {
		lastViewportOpportunityIdsKey = null
		void emitViewportOpportunities()
	},
	{ deep: true }
)

let latestOpportunityFetchRequestId = 0

async function fetchOpportunitiesForMap(): Promise<Opportunity[] | null> {
	const requestId = ++latestOpportunityFetchRequestId
	const view = activeOpportunityView.value.view.value
	const opportunities = (await opportunityStore.fetchByView(view, { projectId: currentProject.value?.id })).items
	return requestId === latestOpportunityFetchRequestId ? opportunities : null
}

function scopedCurrentProject(): Project | null {
	const project = currentProject.value
	return project ?? null
}

function scopedOpportunityProject(): Project | null {
	const project = scopedCurrentProject()
	return project
}

// Resolves once HomePage has loaded the current project, or after a timeout as a fallback.
function waitForCurrentProject(timeoutMs = 8000): Promise<void> {
	if (currentProject.value != null) return Promise.resolve()
	return new Promise<void>(resolve => {
		const stop = watch(currentProject, project => {
			if (project == null) return
			stop()
			clearTimeout(timer)
			resolve()
		})
		const timer = setTimeout(() => {
			stop()
			resolve()
		}, timeoutMs)
	})
}

function currentOpportunitySourceLayer(map: mapboxgl.Map): string | undefined {
	const opportunityLayer = map.getLayer(OPPORTUNITY_LAYER_ID)
	if (!opportunityLayer || !("source-layer" in opportunityLayer)) return undefined
	return (opportunityLayer as { "source-layer"?: string })["source-layer"]
}

function setOpportunityFeatureState(
	map: mapboxgl.Map,
	id: string | number,
	state: Record<string, boolean | number | string | null>
): void {
	const sourceLayer = currentOpportunitySourceLayer(map)
	if (!sourceLayer) return

	map.setFeatureState({ source: MapSourceId.Opportunities, sourceLayer, id }, state)
}

function selectedOpportunityTypes(): OpportunityType[] {
	return activeOpportunityMapTypes.value
}

function opportunityLayerVisibility(): "visible" | "none" {
	return selectedOpportunityTypes().length > 0 ? "visible" : "none"
}

function setOpportunityLayersVisibility(map: mapboxgl.Map, visibility: "visible" | "none"): void {
	for (const layerId of OPPORTUNITY_RENDER_LAYER_IDS) {
		if (map.getLayer(layerId)) {
			map.setLayoutProperty(layerId, "visibility", visibility)
		}
	}
}

function applySelectedOpportunityIconOpacity(map: mapboxgl.Map, selectedOpportunityId: string | number | null): void {
	const opacity: mapboxgl.ExpressionSpecification | number =
		selectedOpportunityId == null
			? 1
			: [
					"case",
					["==", ["to-string", ["get", "id"]], String(selectedOpportunityId)],
					1,
					OPPORTUNITY_ICON_OPACITY_DIMMED,
				]

	for (const layerId of [
		OPPORTUNITY_LAYER_ID,
		OPPORTUNITY_NEW_BADGE_LAYER_ID,
		OPPORTUNITY_OFFER_BADGE_LAYER_ID,
		OPPORTUNITY_LIKED_BADGE_LAYER_ID,
		OPPORTUNITY_DISLIKED_BADGE_LAYER_ID,
	]) {
		if (map.getLayer(layerId)) {
			map.setPaintProperty(layerId, "icon-opacity", opacity)
		}
	}
}

function applySelectedOpportunityIconSize(map: mapboxgl.Map, selectedOpportunityId: string | number | null): void {
	const size: mapboxgl.ExpressionSpecification | number =
		selectedOpportunityId == null
			? OPPORTUNITY_ICON_SIZE
			: [
					"case",
					["==", ["to-string", ["get", "id"]], String(selectedOpportunityId)],
					OPPORTUNITY_ICON_SIZE_SELECTED,
					OPPORTUNITY_ICON_SIZE,
				]

	for (const layerId of [
		OPPORTUNITY_LAYER_ID,
		OPPORTUNITY_NEW_BADGE_LAYER_ID,
		OPPORTUNITY_OFFER_BADGE_LAYER_ID,
		OPPORTUNITY_LIKED_BADGE_LAYER_ID,
		OPPORTUNITY_DISLIKED_BADGE_LAYER_ID,
	]) {
		if (map.getLayer(layerId)) {
			map.setLayoutProperty(layerId, "icon-size", size)
		}
	}
}

function addListQueryParam(params: URLSearchParams, key: string, values: string[]): void {
	if (values.length === 0) return
	params.set(key, values.join(","))
}

function addOptionalRangeQueryParams(
	params: URLSearchParams,
	{
		minKey,
		maxKey,
		range,
		defaultMax = RANGE_MAX,
	}: {
		minKey: string
		maxKey: string
		range?: { min?: number | null; max?: number | null }
		defaultMax?: number
	}
): void {
	if (range?.min != null && Number.isFinite(range.min) && range.min > 0) {
		params.set(minKey, String(range.min))
	}
	if (range?.max != null && Number.isFinite(range.max) && range.max < defaultMax) {
		params.set(maxKey, String(range.max))
	}
}

function addOpportunityTileFilterQueryParams(
	params: URLSearchParams,
	{ includeOpportunityTypes = true }: { includeOpportunityTypes?: boolean } = {}
): void {
	const opportunityFilter = activeOpportunityView.value.view.value.filter?.opportunity

	if (includeOpportunityTypes) {
		addListQueryParam(params, "opportunityTypes", activeOpportunityMapTypes.value)
	}
	addListQueryParam(params, "regions", opportunityFilter?.regions ?? [])
	addListQueryParam(params, "municipalities", opportunityFilter?.municipalities ?? [])
	addListQueryParam(params, "boroughs", opportunityFilter?.boroughs ?? [])
	addListQueryParam(params, "assetTypes", opportunityFilter?.assetTypes ?? [])
	addListQueryParam(params, "landCategories", opportunityFilter?.landCategories ?? [])
	addListQueryParam(params, "landSubcategories", opportunityFilter?.landSubcategories ?? [])
	addListQueryParam(params, "brokers", opportunityFilter?.brokers ?? [])
	addListQueryParam(params, "collaborativeBrokers", opportunityFilter?.collaborativeBrokers ?? [])
	addListQueryParam(params, "evaluationStatus", opportunityFilter?.evaluationStatus ?? [])
	if (opportunityFilter?.priority) {
		params.set("priority", opportunityFilter.priority)
	}
	if (opportunityFilter?.launchDate?.min) {
		params.set("minLaunchDate", opportunityFilter.launchDate.min)
	}
	if (opportunityFilter?.launchDate?.max) {
		params.set("maxLaunchDate", opportunityFilter.launchDate.max)
	}
	addOptionalRangeQueryParams(params, { minKey: "minPrice", maxKey: "maxPrice", range: opportunityFilter?.price })
	addOptionalRangeQueryParams(params, {
		minKey: "minLandArea",
		maxKey: "maxLandArea",
		range: opportunityFilter?.landArea,
	})
	addOptionalRangeQueryParams(params, {
		minKey: "minBuildableLandArea",
		maxKey: "maxBuildableLandArea",
		range: opportunityFilter?.buildableLandArea,
	})
	addOptionalRangeQueryParams(params, {
		minKey: "minResidentialUnits",
		maxKey: "maxResidentialUnits",
		range: opportunityFilter?.residentialUnits,
		defaultMax: Number.POSITIVE_INFINITY,
	})
}

function opportunityVectorTileConfig(map: mapboxgl.Map): { sourceLayer: string; tileUrl: string } {
	const project = scopedOpportunityProject()
	const canReadFullOpportunityTiles = store.isUserLoggedIn && store.currentUser.has(Permission.OpportunityRead)
	const usesFullOpportunityTiles = Boolean(project || canReadFullOpportunityTiles)
	const sourceLayer = usesFullOpportunityTiles ? OPPORTUNITY_SOURCE_LAYER : OPPORTUNITY_PUBLIC_SOURCE_LAYER

	if (!usesFullOpportunityTiles) {
		return {
			sourceLayer,
			tileUrl: `${PUBLIC_TILES_URL_LIVE}/tiles.opportunity_public/{z}/{x}/{y}.pbf`,
		}
	}

	const params = new URLSearchParams()
	if (project) {
		params.set("projectId", project.id)
	}
	addOpportunityTileFilterQueryParams(params, {
		includeOpportunityTypes: map.getZoom() < OPPORTUNITY_CLIENT_FILTER_MIN_ZOOM,
	})
	params.set("refreshKey", String(store.opportunityTileRefreshKey))

	const queryString = params.toString()
	return {
		sourceLayer,
		tileUrl: `${TILES_URL_LIVE}/opportunities/{z}/{x}/{y}.pbf${queryString ? `?${queryString}` : ""}`,
	}
}

function isPendingOpportunityTileFeature(feature: MapboxGeoJSONFeature | undefined): boolean {
	const isSynced = feature?.properties?.is_synced
	return isSynced === false || isSynced === 0 || isSynced === "false"
}

function isDuplicateOpportunityClick(opportunityId: string, event: MapMouseEvent | MapTouchEvent): boolean {
	const now = performance.now()
	const isDuplicate =
		lastOpportunityClick?.id === opportunityId &&
		lastOpportunityClick.x === event.point.x &&
		lastOpportunityClick.y === event.point.y &&
		now - lastOpportunityClick.time < 250

	lastOpportunityClick = { id: opportunityId, x: event.point.x, y: event.point.y, time: now }
	return isDuplicate
}

function recentOpportunityFilter(): mapboxgl.FilterSpecification {
	const cutoffDate = new Date(Date.now() - NEW_OPPORTUNITY_BADGE_WINDOW_MS).toISOString().slice(0, 10)

	return [
		"all",
		["has", "launch_date"],
		[">=", ["to-string", ["get", "launch_date"]], cutoffDate],
		["!=", ["get", "evaluation_status"], OpportunityStatus.Like],
		["!=", ["get", "evaluation_status"], OpportunityStatus.Dislike],
	]
}

function offerOpportunityFilter(): mapboxgl.FilterSpecification {
	return ["==", ["get", "sales_process_status"], "accepted_psa"]
}

function likedOpportunityFilter(): mapboxgl.FilterSpecification {
	return ["==", ["get", "evaluation_status"], OpportunityStatus.Like]
}

function dislikedOpportunityFilter(): mapboxgl.FilterSpecification {
	return ["==", ["get", "evaluation_status"], OpportunityStatus.Dislike]
}

function opportunityTypeFilter(): mapboxgl.FilterSpecification {
	const types = selectedOpportunityTypes().flatMap(type =>
		type === OpportunityType.ListingLanderz ? [type, "landerz", "landerz_listing"] : [type]
	)
	if (types.length === 0) return ["==", ["get", "opportunity_type"], ""]
	return ["match", ["get", "opportunity_type"], types, true, false]
}

function withOpportunityTypeFilter(filter: mapboxgl.FilterSpecification): mapboxgl.FilterSpecification {
	return ["all", filter, opportunityTypeFilter()]
}

function applyOpportunityTypeFilters(map: mapboxgl.Map): void {
	const layerFilters: Array<[string, mapboxgl.FilterSpecification]> = [
		[OPPORTUNITY_LAYER_ID, withOpportunityTypeFilter(unclusteredOpportunityFilter())],
		[OPPORTUNITY_NEW_BADGE_LAYER_ID, withOpportunityTypeFilter(unclusteredBadgeFilter(recentOpportunityFilter()))],
		[OPPORTUNITY_OFFER_BADGE_LAYER_ID, withOpportunityTypeFilter(unclusteredBadgeFilter(offerOpportunityFilter()))],
		[OPPORTUNITY_LIKED_BADGE_LAYER_ID, withOpportunityTypeFilter(unclusteredBadgeFilter(likedOpportunityFilter()))],
		[
			OPPORTUNITY_DISLIKED_BADGE_LAYER_ID,
			withOpportunityTypeFilter(unclusteredBadgeFilter(dislikedOpportunityFilter())),
		],
	]

	for (const [layerId, filter] of layerFilters) {
		if (map.getLayer(layerId)) map.setFilter(layerId, filter)
	}
}

function clusteredOpportunityFilter(): mapboxgl.FilterSpecification {
	return ["==", ["get", "is_cluster"], true]
}

function unclusteredOpportunityFilter(): mapboxgl.FilterSpecification {
	return ["!=", ["get", "is_cluster"], true]
}

function unclusteredBadgeFilter(filter: mapboxgl.FilterSpecification): mapboxgl.FilterSpecification {
	return ["all", unclusteredOpportunityFilter(), filter]
}

function removeSourceLayers(map: mapboxgl.Map, sourceId: string): void {
	const style = map.getStyle()
	const sourceLayers = style.layers.filter(layer => layer.source === sourceId)

	for (const layer of sourceLayers) {
		if (map.getLayer(layer.id)) {
			map.removeLayer(layer.id)
		}
	}
	if (map.getSource(sourceId)) {
		map.removeSource(sourceId)
	}
}

function addOpportunityBadgeLayer(
	map: mapboxgl.Map,
	{
		layerId,
		sourceLayer,
		iconImage,
		iconOffset,
		filter,
	}: {
		layerId: string
		sourceLayer: string
		iconImage: string
		iconOffset: [number, number]
		filter: mapboxgl.FilterSpecification
	}
): void {
	map.addLayer({
		id: layerId,
		type: "symbol",
		source: MapSourceId.Opportunities,
		"source-layer": sourceLayer,
		slot: "top",
		filter,
		layout: {
			visibility: opportunityLayerVisibility(),
			"icon-image": iconImage,
			"icon-size": OPPORTUNITY_ICON_SIZE,
			"icon-offset": iconOffset,
			"icon-anchor": "center",
			"icon-allow-overlap": true,
			"icon-ignore-placement": true,
		},
		paint: {
			"icon-opacity": 1,
		},
		metadata: { custom: true },
	})
}

function addOpportunityClusterLayers(map: mapboxgl.Map, sourceLayer: string): void {
	const rootStyle = window.getComputedStyle(document.documentElement)
	const accentColor = rootStyle.getPropertyValue("--primary-green").trim() || "#0b6e4f"
	const lightColor = rootStyle.getPropertyValue("--neutral-0").trim() || "#ffffff"
	const radius: mapboxgl.ExpressionSpecification = ["step", ["get", "point_count"], 14, 10, 17, 50, 20, 200, 23]

	map.addLayer({
		id: OPPORTUNITY_CLUSTER_SHADOW_LAYER_ID,
		type: "circle",
		source: MapSourceId.Opportunities,
		"source-layer": sourceLayer,
		slot: "top",
		filter: clusteredOpportunityFilter(),
		layout: {
			visibility: opportunityLayerVisibility(),
			"circle-sort-key": ["get", "point_count"],
		},
		paint: {
			"circle-color": lightColor,
			"circle-radius": ["+", radius, 5],
			"circle-blur": 0.6,
			"circle-opacity": 0.5,
		},
		metadata: { custom: true },
	})

	map.addLayer({
		id: OPPORTUNITY_CLUSTER_LAYER_ID,
		type: "circle",
		source: MapSourceId.Opportunities,
		"source-layer": sourceLayer,
		slot: "top",
		filter: clusteredOpportunityFilter(),
		layout: {
			visibility: opportunityLayerVisibility(),
			"circle-sort-key": ["get", "point_count"],
		},
		paint: {
			"circle-color": accentColor,
			"circle-radius": radius,
			"circle-stroke-color": lightColor,
			"circle-stroke-width": 1,
		},
		metadata: { custom: true },
	})

	map.addLayer({
		id: OPPORTUNITY_CLUSTER_COUNT_LAYER_ID,
		type: "symbol",
		source: MapSourceId.Opportunities,
		"source-layer": sourceLayer,
		slot: "top",
		filter: clusteredOpportunityFilter(),
		layout: {
			visibility: opportunityLayerVisibility(),
			"text-field": ["get", "point_count_abbreviated"],
			"text-font": ["Open Sans Bold"],
			"text-size": ["step", ["get", "point_count"], 12, 10, 13, 200, 14],
			"symbol-sort-key": ["*", -1, ["to-number", ["get", "point_count"]]],
			"text-allow-overlap": false,
			"text-ignore-placement": false,
			"text-padding": 4,
		},
		paint: {
			"text-color": lightColor,
		},
		metadata: { custom: true },
	})
}

function refreshOpportunityVectorTiles(map: mapboxgl.Map): void {
	const { tileUrl } = opportunityVectorTileConfig(map)
	if (currentOpportunityTileUrl === tileUrl && map.getSource(MapSourceId.Opportunities)) {
		return
	}

	removeSourceLayers(map, MapSourceId.Opportunities)
	addOpportunityVectorTiles(map)
}

function addOpportunityVectorTiles(map: mapboxgl.Map): void {
	const { sourceLayer, tileUrl } = opportunityVectorTileConfig(map)
	currentOpportunityTileUrl = tileUrl

	map.addSource(MapSourceId.Opportunities, {
		type: "vector",
		tiles: [tileUrl],
		promoteId: "id",
	})
	addOpportunityClusterLayers(map, sourceLayer)
	map.addLayer({
		id: OPPORTUNITY_LAYER_ID,
		type: "symbol",
		source: MapSourceId.Opportunities,
		"source-layer": sourceLayer,
		slot: "top",
		filter: unclusteredOpportunityFilter(),
		layout: {
			visibility: opportunityLayerVisibility(),
			"icon-image": [
				"match",
				["get", "opportunity_type"],
				OpportunityType.ListingLanderz,
				OPPORTUNITY_TYPE_ICON_NAMES[OpportunityType.ListingLanderz],
				OpportunityType.ListingPotential,
				OPPORTUNITY_TYPE_ICON_NAMES[OpportunityType.ListingPotential],
				OpportunityType.OffMarketOfficial,
				OPPORTUNITY_TYPE_ICON_NAMES[OpportunityType.OffMarketOfficial],
				OpportunityType.OpportunityTarget,
				OPPORTUNITY_TYPE_ICON_NAMES[OpportunityType.OpportunityTarget],
				OPPORTUNITY_TYPE_ICON_NAMES[OpportunityType.ListingLanderz],
			],
			"icon-size": OPPORTUNITY_ICON_SIZE,
			"icon-anchor": "center",
			"icon-allow-overlap": true,
			"icon-ignore-placement": true,
		},
		paint: {
			"icon-opacity": 0.95,
		},
		metadata: { custom: true },
	})
	addOpportunityBadgeLayer(map, {
		layerId: OPPORTUNITY_NEW_BADGE_LAYER_ID,
		sourceLayer,
		iconImage: OPPORTUNITY_NEW_BADGE_IMAGE_ID,
		iconOffset: [19, -12],
		filter: unclusteredBadgeFilter(recentOpportunityFilter()),
	})
	addOpportunityBadgeLayer(map, {
		layerId: OPPORTUNITY_OFFER_BADGE_LAYER_ID,
		sourceLayer,
		iconImage: OPPORTUNITY_OFFER_BADGE_IMAGE_ID,
		iconOffset: [19, 13],
		filter: unclusteredBadgeFilter(offerOpportunityFilter()),
	})
	addOpportunityBadgeLayer(map, {
		layerId: OPPORTUNITY_LIKED_BADGE_LAYER_ID,
		sourceLayer,
		iconImage: OPPORTUNITY_LIKED_BADGE_IMAGE_ID,
		iconOffset: [-19, -12],
		filter: unclusteredBadgeFilter(likedOpportunityFilter()),
	})
	addOpportunityBadgeLayer(map, {
		layerId: OPPORTUNITY_DISLIKED_BADGE_LAYER_ID,
		sourceLayer,
		iconImage: OPPORTUNITY_DISLIKED_BADGE_IMAGE_ID,
		iconOffset: [19, -12],
		filter: unclusteredBadgeFilter(dislikedOpportunityFilter()),
	})
	applyOpportunityTypeFilters(map)
	applySelectedOpportunityIconSize(map, selectedOpportunityId.value)
	applySelectedOpportunityIconOpacity(map, selectedOpportunityId.value)
}

// Transactions
function addTransactions(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers: _,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/tiles.transaction/{z}/{x}/{y}.pbf`],
		promoteId: "presentation_id",
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "tiles.transaction",
		type: "fill",
		slot: "top",
		layout: {
			visibility: "none",
			// Draw assembly transactions above standalone ones when polygons overlap.
			"fill-sort-key": ["case", ["==", ["get", "composition"], "assembly"], 1, 0],
		},
		paint: {
			"fill-color": "#E6007E",
			"fill-opacity": [
				"interpolate",
				["linear"],
				["zoom"],
				10, // at zoom level 10 ⤵
				[
					"case",
					["any", ["boolean", ["feature-state", "highlight"], false], ["boolean", ["feature-state", "preview"], false]],
					1.0, // highlight / preview opacity
					0.5, // default opacity
				],
				13, // at zoom level 13 ⤵
				[
					"case",
					["any", ["boolean", ["feature-state", "highlight"], false], ["boolean", ["feature-state", "preview"], false]],
					0.5, // highlight / preview opacity
					0.2, // default opacity
				],
			],
		},
	})
}

function addCompletedAssessments(
	map: mapboxgl.Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_models.completed_assessments/{z}/{x}/{y}.pbf`],
		promoteId: "hubspot_id",
	})
	const visibility = visibleLayers.includes(LayerType.CompletedAssessments) ? "visible" : "none"

	map.addLayer({
		id: `${layerName}-halo`,
		source: sourceName,
		"source-layer": "landerz_models.completed_assessments",
		type: "circle",
		layout: { visibility },
		paint: {
			"circle-color": COMPLETED_ASSESSMENTS_COLOR,
			"circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 5, 10, 7, 14, 10, 18, 16, 22, 24],
			"circle-opacity": 0.18,
			"circle-blur": 0.55,
		},
		metadata: { custom: true },
	})

	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_models.completed_assessments",
		type: "circle",
		layout: { visibility },
		paint: {
			"circle-color": COMPLETED_ASSESSMENTS_COLOR,
			"circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 2.5, 10, 3.5, 14, 5.5, 18, 9, 22, 14],
			"circle-opacity": 0.92,
			"circle-stroke-color": "#ffffff",
			"circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 4, 0.75, 14, 1.5, 18, 2.25, 22, 3],
			"circle-stroke-opacity": 0.95,
		},
		metadata: { custom: true },
	})
}

// Format dates to "YYYY-MM-DD HH:mm:ss"
function formatDate(date: Date) {
	return (
		`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")} ` +
		`${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`
	)
}

function withSelectedTransactionVisibility(
	filter: TransactionTileFilter,
	selectedTransaction: Transaction | null | undefined
): TransactionTileFilter {
	if (!selectedTransaction?.inscriptionNumber) return filter

	return [
		"any",
		["==", ["get", "inscription_number"], selectedTransaction.inscriptionNumber],
		filter,
	] as unknown as TransactionTileFilter
}

function transactionFilterForView(
	view: View,
	currentUser: User | null,
	selectedTransaction?: Transaction | null
): TransactionTileFilter {
	const filter = view.filter?.transaction
	const price = filter?.price
	const landArea = filter?.landArea
	const date = filter?.date
	const regions = filter?.regions ?? []
	const municipalities = filter?.municipalities ?? []
	const boroughs = filter?.boroughs ?? []
	const statuses = filter?.statuses ?? []
	const assetTypes = filter?.assetTypes ?? []
	const builtCategories = filter?.builtCategories ?? []
	const builtSubcategories = filter?.builtSubcategories ?? []
	const builtCategoryBuckets = builtAssetBuckets(builtCategories)
	const builtSubcategoryBucketValues = builtSubcategoryBuckets(builtSubcategories)
	const landCategories = filter?.landCategories ?? []
	const landSubcategories = filter?.landSubcategories ?? []

	const transactionFilter = [
		"all",
		transactionBuildingPriceFilter(filter),
		transactionRentalIncomeFilter(filter),
		transactionParkingFilter(filter),
		hasDefinedNumberRange(price, RANGE_MAX)
			? [
					"all",
					["has", "sale_price"],
					["!=", ["get", "sale_price"], null],
					[">=", ["to-number", ["get", "sale_price"]], price?.min ?? 0],
					["<=", ["to-number", ["get", "sale_price"]], price?.max ?? RANGE_MAX],
				]
			: true,
		hasDefinedDateRange(date)
			? [
					"all",
					[">=", ["get", "deed_of_sale_date"], formatDate(date?.min ? new Date(`${date.min}T00:00:00`) : new Date(0))],
					[
						"<=",
						["get", "deed_of_sale_date"],
						formatDate(date?.max ? new Date(`${date.max}T00:00:00`) : new Date(8.64e15)),
					],
				]
			: true,
		hasDefinedNumberRange(landArea, RANGE_MAX)
			? [
					"all",
					["has", "area_square_feet"],
					["!=", ["get", "area_square_feet"], null],
					[">=", ["to-number", ["get", "area_square_feet"]], landArea?.min ?? 0],
					["<=", ["to-number", ["get", "area_square_feet"]], landArea?.max ?? RANGE_MAX],
				]
			: true,
		hasDefinedNumberRange(filter?.buildableLandArea, RANGE_MAX)
			? [
					"all",
					["has", "buildable_area_sqft"],
					["!=", ["get", "buildable_area_sqft"], null],
					[">=", ["to-number", ["get", "buildable_area_sqft"]], filter?.buildableLandArea?.min ?? 0],
					["<=", ["to-number", ["get", "buildable_area_sqft"]], filter?.buildableLandArea?.max ?? RANGE_MAX],
				]
			: true,
		hasDefinedNumberRange(filter?.residentialUnits, RESIDENTIAL_UNITS_SLIDER_MAX)
			? [
					"all",
					["has", "number_of_units"],
					["!=", ["get", "number_of_units"], null],
					[">=", ["to-number", ["get", "number_of_units"]], filter?.residentialUnits?.min ?? 0],
					filter?.residentialUnits?.max != null
						? ["<=", ["to-number", ["get", "number_of_units"]], filter.residentialUnits.max]
						: true,
				]
			: true,
		numberRangeTileFilter("price_per_unit", filter?.pricePerUnit),
		numberRangeTileFilter("building_area_sqft", filter?.buildingArea),
		buildingAgeTileFilter(filter?.buildingAge),
		propertyUseTileFilter(filter?.propertyUses ?? []),
		regions.length > 0 ? ["all", ["has", "region_name"], ["in", ["get", "region_name"], ["literal", regions]]] : true,
		municipalities.length > 0 ? ["in", ["get", "municipality_name"], ["literal", municipalities]] : true,
		boroughs.length > 0 ? ["in", ["get", "borough"], ["literal", boroughs]] : true,
		currentUser?.has(Permission.TransactionWrite) && statuses.length > 0
			? ["in", ["get", "metadata_status"], ["literal", statuses]]
			: true,
		filter?.verificationStatus === TransactionVerificationStatus.Verified
			? ["==", ["get", "metadata_status"], "done"]
			: filter?.verificationStatus === TransactionVerificationStatus.Unverified
				? ["!=", ["get", "metadata_status"], "done"]
				: true,
		filter?.source ? ["==", ["get", "source"], filter.source] : true,
		filter?.composition ? ["==", ["get", "composition"], filter.composition] : true,
		filter?.buyerStatus ? ["==", ["get", "metadata_buyer_status"], filter.buyerStatus] : true,
		currentUser?.has(Permission.TransactionRead) && config.ui.has(Feature.DevTypes)
			? assetTypes.length > 0
				? [
						"any",
						...assetTypes.filter(type => type !== "none").map(type => ["in", type, ["get", "asset_type"]]),
						...(assetTypes.includes("none")
							? [
									["!", ["has", "asset_type"]],
									["==", ["get", "asset_type"], "{}"],
								]
							: []),
					]
				: true
			: true,
		currentUser?.has(Permission.TransactionRead) && config.ui.has(Feature.DevTypes)
			? landCategories.length > 0
				? ["any", ...landCategories.map(category => ["in", category, ["get", "land_category_new"]])]
				: true
			: true,
		currentUser?.has(Permission.TransactionRead) && config.ui.has(Feature.DevTypes)
			? landSubcategories.length > 0
				? ["any", ...landSubcategories.map(subcategory => ["in", subcategory, ["get", "land_subcategory"]])]
				: true
			: true,
		currentUser?.has(Permission.TransactionRead) && config.ui.has(Feature.DevTypes)
			? builtCategoryBuckets.length > 0
				? ["any", ...builtCategoryBuckets.map(category => ["in", category, ["get", "asset_category"]])]
				: true
			: true,
		currentUser?.has(Permission.TransactionRead) && config.ui.has(Feature.DevTypes)
			? builtSubcategoryBucketValues.length > 0
				? ["any", ...builtSubcategoryBucketValues.map(bucket => ["in", bucket, ["get", "asset_category"]])]
				: true
			: true,
	] as unknown as TransactionTileFilter

	return withSelectedTransactionVisibility(transactionFilter, selectedTransaction)
}

function applyTransactionViewFilter(map: mapboxgl.Map, selectedTransaction?: Transaction | null): void {
	if (!map.getLayer("transaction-fill")) return
	if (!store.currentUser?.has(Permission.TransactionRead)) {
		map.setLayoutProperty("transaction-fill", "visibility", "none")
		return
	}
	if (!filters.value.layers.includes(LayerType.Transaction)) {
		map.setLayoutProperty("transaction-fill", "visibility", "none")
		return
	}
	map.setLayoutProperty("transaction-fill", "visibility", "visible")
	map.setFilter(
		"transaction-fill",
		transactionFilterForView(activeTransactionView.value.view.value, store.currentUser, selectedTransaction)
	)
}

function applyActiveTransactionFilters(map: mapboxgl.Map, selectedTransaction?: Transaction | null): void {
	applyTransactionViewFilter(map, selectedTransaction)
}

function clearPendingTransactionSourceLoadFilterRefresh(map: mapboxgl.Map): void {
	if (!pendingTransactionSourceLoadFilterRefresh) return

	map.off(MapEventName.SourceData, pendingTransactionSourceLoadFilterRefresh)
	pendingTransactionSourceLoadFilterRefresh = null
}

function applyTransactionFiltersWhenSourceLoads(map: mapboxgl.Map, transaction: Transaction | null | undefined): void {
	clearPendingTransactionSourceLoadFilterRefresh(map)
	if (!transaction || !map.getSource(MapSourceId.Transactions) || map.isSourceLoaded(MapSourceId.Transactions)) return

	pendingTransactionSourceLoadFilterRefresh = event => {
		if (event.sourceId !== MapSourceId.Transactions || !map.isSourceLoaded(MapSourceId.Transactions)) return

		clearPendingTransactionSourceLoadFilterRefresh(map)
		applyActiveTransactionFilters(map, transaction)
	}

	map.on(MapEventName.SourceData, pendingTransactionSourceLoadFilterRefresh)
}

let touchStartPoint: mapboxgl.Point

// trackTouchDrag sets up a touch event so that onMapClick may compare the end
// point with the start point.
function trackTouchDrag(map: mapboxgl.Map) {
	map.on("touchstart", event => (touchStartPoint = event.point))
}

const LONG_PRESS_DURATION_MS = 500
let longPressTimer: ReturnType<typeof setTimeout> | null = null
let longPressStartPoint: mapboxgl.Point | null = null
let longPressFired = false

function trackLongPress(map: mapboxgl.Map, canLongPress: () => boolean) {
	map.getCanvas().addEventListener("contextmenu", event => event.preventDefault())
	map.on("touchstart", "cadastre", event => {
		if (!canLongPress()) return
		// second finger landing on the parcel (pinch/rotate) shouldn't count as a long press
		if ((event.originalEvent as TouchEvent | undefined)?.touches?.length > 1) return

		// mapbox deletes event.features right after this listener returns, so it must be captured now
		const capturedEvent = { features: event.features, lngLat: event.lngLat }

		longPressStartPoint = event.point
		longPressFired = false
		if (longPressTimer) clearTimeout(longPressTimer)
		longPressTimer = setTimeout(() => {
			longPressFired = true
			selectionModeActive.value = true
			selectParcel(map, capturedEvent)
		}, LONG_PRESS_DURATION_MS)
	})
	map.on("touchmove", event => {
		if (longPressStartPoint && longPressTimer && isDrag(longPressStartPoint, event.point)) {
			clearTimeout(longPressTimer)
			longPressTimer = null
		}
	})
	const cancelLongPress = () => {
		if (longPressTimer) clearTimeout(longPressTimer)
		longPressTimer = null
	}
	map.on("touchend", cancelLongPress)
	map.on("touchcancel", cancelLongPress)
}

// isDrag verifies that two points on the map are far enough appart as to
// represent a dragging event.
//
//                * (end)
//               /
//              /
//             /
//            /
//   (start) *
//
// In the representation above, assuming each char or row is one pixel,
// dragging would have moved x=5 y=5.
//
//                * (end)
//               /|
//              / |
//             /  |
//            /   |
//   (start) *----|
//
// The hypotenuse ends up mesuring
//
// 		√(start²+end²) = √(5²+5²) ≈ 7.07
//
// which is considered a drag in this function since it is over 5..
// The cutoff is set to five semi-arbitrarily. Adjust as needed.
function isDrag(startPoint: mapboxgl.Point, endPoint: mapboxgl.Point) {
	const dx = endPoint.x - startPoint.x
	const dy = endPoint.y - startPoint.y

	return Math.hypot(dx, dy) > 5
}

// onMapClick "fixes" map.on("click") by handling touch events as well. It also
// ensures that the touch isn't a drag event, in which case it doesn't trigger.
//
// Usage:
//		onMapClick(map, "layer-name", event => {})
//
// The layer name may be falsy, in which case the handler applies to all
// layers.
//
// NOTE online research suggests that "click" should handle touch events as well,
// but this hasn't been working for us. If the true cause is ever found, it
// should replace the use of this workaround function.
//
// prettier-ignore

function onMapClick(map: mapboxgl.Map, layer: string | null, cb: (event: MapMouseEvent | MapTouchEvent) => void) {
	const touchHandler = (event: MapMouseEvent | MapTouchEvent) => {

		if (touchStartPoint != null && isDrag(touchStartPoint, event.point)) return
		cb(event)
	}
	if (layer) {
		map.on("touchend", layer, touchHandler)
		map.on("click", layer, cb)
	} else {
		map.on("touchend", touchHandler)
		map.on("click", cb)
	}
}

// Refreshes through the active View and ignores responses superseded by a newer request.
async function refreshOpportunitiesLayer(): Promise<boolean> {
	const map = mapRef.value
	if (!map) {
		console.warn("Map not available")
		return false
	}

	try {
		const freshOpportunities = await fetchOpportunitiesForMap()
		if (freshOpportunities == null) return false

		storeOpportunities.value = freshOpportunities as any
		const source = map.getSource(MapSourceId.Opportunities)
		if (source) {
			store.refreshOpportunityTiles()
			currentOpportunityTileUrl = null
			refreshOpportunityVectorTiles(map)
			// querySourceFeatures (used right after this resolves, e.g. to select/fly to a newly created target)
			// needs the map to have actually processed the source update first
			await new Promise<void>(resolve => map.once("idle", () => resolve()))

			// Now we have created a prospect when we selected a parcel
			// So we need clear parcel markers
			for (const marker of parcelMarkers.value.values()) {
				marker.remove()
			}
			parcelMarkers.value.clear()
		} else {
			console.warn("Opportunities source not found on map")
		}
		return true
	} catch (error) {
		console.error("Failed to refresh opportunities layer:", error)
		return false
	}
}

watch(projectOpportunitiesInvalidation, async invalidation => {
	if (!invalidation || invalidation.projectId !== currentProject.value?.id) return

	const refreshed = await refreshOpportunitiesLayer()
	if (!refreshed || projectOpportunitiesInvalidation.value?.revision !== invalidation.revision) return

	const opportunityId = invalidation.selectOpportunityId
	if (
		opportunityId != null &&
		storeOpportunities.value.some(opportunity => String(opportunity.id) === String(opportunityId))
	) {
		selectedOpportunityId.value = opportunityId
	}
})

const pendingTargetSyncIds = new Map<string, number>()
const PENDING_TARGET_SYNC_POLL_INTERVAL_MS = 25_000
const PENDING_TARGET_SYNC_GIVE_UP_MS = 45 * 60_000
const PENDING_TARGET_SYNC_STORAGE_KEY = "landerz.pendingTargetSyncIds"
let pendingTargetSyncTimer: ReturnType<typeof setInterval> | null = null

function persistPendingTargetSyncIds() {
	try {
		localStorage.setItem(PENDING_TARGET_SYNC_STORAGE_KEY, JSON.stringify(Object.fromEntries(pendingTargetSyncIds)))
	} catch {
		// ignore
	}
}

function loadPersistedPendingTargetSyncIds(): [string, number][] {
	try {
		const raw = localStorage.getItem(PENDING_TARGET_SYNC_STORAGE_KEY)
		return raw ? Object.entries(JSON.parse(raw)) : []
	} catch {
		return []
	}
}

function registerPendingTargetSync(id: string | number) {
	pendingTargetSyncIds.set(String(id), Date.now())
	persistPendingTargetSyncIds()
	if (!pendingTargetSyncTimer) {
		pendingTargetSyncTimer = setInterval(pollPendingTargetSync, PENDING_TARGET_SYNC_POLL_INTERVAL_MS)
	}
}

function resumePendingTargetSyncFromStorage() {
	const persisted = loadPersistedPendingTargetSyncIds()
	if (persisted.length === 0) return

	for (const [id, registeredAt] of persisted) {
		pendingTargetSyncIds.set(id, registeredAt)
	}
	void pollPendingTargetSync()
	if (!pendingTargetSyncTimer) {
		pendingTargetSyncTimer = setInterval(pollPendingTargetSync, PENDING_TARGET_SYNC_POLL_INTERVAL_MS)
	}
}

function stopPendingTargetSyncPolling() {
	if (pendingTargetSyncTimer) {
		clearInterval(pendingTargetSyncTimer)
		pendingTargetSyncTimer = null
	}
}

async function pollPendingTargetSync() {
	await refreshOpportunitiesLayer()

	const now = Date.now()
	for (const [id, registeredAt] of pendingTargetSyncIds) {
		const opportunity = storeOpportunities.value.find(o => String(o.id) === id)

		if (opportunity && !opportunity.isPlaceholder) {
			pendingTargetSyncIds.delete(id)
			const label = opportunity.address || opportunity.city || `#${opportunity.hubspot_record_id}`
			toastStore.success("opportunities.sync.completed", {
				params: { label },
				icon: IconName.Target,
				// Fires from a background poll, so don't auto-dismiss and risk losing the map link.
				duration: 0,
				action: {
					labelKey: "opportunities.sync.viewOnMap",
					onClick: () => {
						selectedOpportunityId.value = opportunity.id
					},
				},
			})
		} else if (now - registeredAt > PENDING_TARGET_SYNC_GIVE_UP_MS) {
			pendingTargetSyncIds.delete(id)
		}
	}

	persistPendingTargetSyncIds()
	if (pendingTargetSyncIds.size === 0) {
		stopPendingTargetSyncPolling()
	}
}

function refreshProspectsLayer() {
	const map = mapRef.value

	if (!map) {
		return
	}

	refreshSource(map, MapSourceId.Prospects)
	setProspectFeatureStates(map)
}

onMounted(async () => {
	await nextTick()

	if (!mapboxgl.supported()) {
		Sentry.captureMessage("MaboxGL is not supported", "error")
		alert(
			"This application requires WebGL, which your browser does not support. Please try again with a different browser. \n\nCette application utilise WebGL, qui n'est pas supporté par votre navigateur. Veuillez réessayer avec un autre navigateur."
		)
	}

	const map: mapboxgl.Map = new mapboxgl.Map({
		// HTML element id to insert mapbox into
		container: "map",

		// Mapbox API token
		accessToken: import.meta.env.VITE_MAPBOX_TOKEN,

		// A custom one is instead added below
		attributionControl: false,

		// Keeps camera position in URL hash
		hash: true,

		// Camera position to initialize map at
		center: {
			lng: cameraPosition.value.centerLongitude || -73.57786,
			lat: cameraPosition.value.centerLatitude || 45.49729,
		},
		zoom: cameraPosition.value.zoom || (isLargeScreen.value ? 15 : 9), // default: a zoom level that loads less parcels on the cadastre
		bearing: cameraPosition.value.bearing || 0,
		pitch: cameraPosition.value.pitch || 0,

		// Map style to use (e.g. mapbox standard, carto positron, mapbox satellite)
		style: BASE_MAP_STYLES[currentStyle.value],

		// Callback run before every request to an external URL.
		// Used here to pass in the auth token to our API service for
		// authenticated routes.
		transformRequest: (url: string) => {
			return {
				url,
				headers: tileRequestHeaders({
					url,
					apiToken: store.userApiToken,
				}),
			}
		},
	})
	mapRef.value = map
	mapCenter.value = map.getCenter()
	map.on("move", pulseMoveHandler)
	// Add a compact attribution on the map (defaults to bottom right
	// corner). Add the "Landerz" string as well, as attribution for
	// the additional data layers.
	map.addControl(
		new mapboxgl.AttributionControl({
			compact: true,
			customAttribution: ["<a href='https://landerz.ca/' target=_blank>Landerz</a>", store.currentUser.role],
		})
	)

	// drawing tools

	let isDrawing = false

	const draw = new MapboxDraw({
		defaultMode: MapboxDraw.constants.modes.SIMPLE_SELECT,
		displayControlsDefault: false,
	})
	map.addControl(draw, "bottom-right")
	drawInstance.value = draw

	// timestamp of the last completed measure, so the click that finishes it
	// (often a double-click, sometimes landing on a feature) doesn't fall
	// through to the feature click handlers underneath.
	let measureCompletedAt = 0

	trackTouchDrag(map)
	trackLongPress(map, () => !isDrawing && draw.getMode() === MapboxDraw.constants.modes.SIMPLE_SELECT)

	const updateDrawMeasurements = () => {
		const data = draw.getAll()

		if (data.features.length === 0) return

		const selectedFeatures = draw.getSelected().features
		const feature = selectedFeatures.length > 0 ? selectedFeatures[0] : data.features[data.features.length - 1]

		const mode = draw.getMode()

		const isPolygonMode =
			mode === MapboxDraw.constants.modes.DRAW_POLYGON ||
			(feature.geometry.type === "Polygon" &&
				(mode === MapboxDraw.constants.modes.SIMPLE_SELECT || mode === MapboxDraw.constants.modes.DIRECT_SELECT))

		const isLineMode =
			mode === MapboxDraw.constants.modes.DRAW_LINE_STRING ||
			(feature.geometry.type === "LineString" &&
				(mode === MapboxDraw.constants.modes.SIMPLE_SELECT || mode === MapboxDraw.constants.modes.DIRECT_SELECT))

		if (isPolygonMode && feature.geometry.type === "Polygon") {
			if (feature.geometry.coordinates[0].length > 3) {
				const areaSquareMeters = getArea({
					type: "FeatureCollection",
					features: [feature],
				})

				drawInfo.value.areaSquareMeters = areaSquareMeters
				drawInfo.value.type = MapboxDraw.constants.modes.DRAW_POLYGON

				if (drawInfoBox?.value) {
					drawInfoBox.value.hidden = false
				}
			}
		} else if (isLineMode && feature.geometry.type === "LineString") {
			if (feature.geometry.coordinates.length > 1) {
				const distanceMeters = getLength(feature, { units: "kilometers" }) * 1000

				drawInfo.value.distanceMeters = distanceMeters
				drawInfo.value.type = MapboxDraw.constants.modes.DRAW_LINE_STRING

				if (drawInfoBox?.value) {
					drawInfoBox.value.hidden = false
				}
			}
		}
	}

	map.on("draw.modechange" as any, event => {
		const { mode } = event

		// Mode constants: https://github.com/mapbox/mapbox-gl-draw/blob/main/src/constants.js#L49
		switch (mode) {
			case MapboxDraw.constants.modes.DRAW_POLYGON: {
				isDrawing = true
				mapMeasuring.value = true
				drawInfo.value.type = MapboxDraw.constants.modes.DRAW_POLYGON
				drawInfo.value.areaSquareMeters = 0
				if (drawInfoBox?.value) {
					drawInfoBox.value.hidden = false
				}
				break
			}
			case MapboxDraw.constants.modes.DRAW_LINE_STRING: {
				isDrawing = true
				mapMeasuring.value = true
				drawInfo.value.type = MapboxDraw.constants.modes.DRAW_LINE_STRING
				drawInfo.value.distanceMeters = 0
				if (drawInfoBox?.value) {
					drawInfoBox.value.hidden = false
				}
				break
			}
			case MapboxDraw.constants.modes.SIMPLE_SELECT: {
				mapMeasuring.value = false
				measureCompletedAt = performance.now()
				// Process on the next event loop so that the second click
				// (to end a polygon) doesn't trigger other map features.
				setTimeout(() => (isDrawing = false))
				if (drawInfoBox?.value) {
					drawInfoBox.value.hidden = true
				}
				break
			}
		}
	})

	map.on("draw.render" as any, updateDrawMeasurements)

	map.on("draw.update" as any, updateDrawMeasurements)

	map.on("draw.create" as any, event => {
		const { features } = event

		measureCompletedAt = performance.now()

		const mode = draw.getMode()

		switch (mode) {
			case MapboxDraw.constants.modes.DRAW_POLYGON: {
				const areaSquareMeters = getArea({
					type: "FeatureCollection",
					features: event.features,
				})

				drawInfo.value.areaSquareMeters = areaSquareMeters

				break
			}
			case MapboxDraw.constants.modes.DRAW_LINE_STRING: {
				const feature = features[0]
				const distanceMeters = getLength(feature, { units: "kilometers" }) * 1000

				drawInfo.value.distanceMeters = distanceMeters

				break
			}
		}
	})

	map.on("draw.selectionchange" as any, event => {
		if (event.features.length > 0) {
			updateDrawMeasurements()
		}
	})

	map.on("draw.delete" as any, () => {
		drawInfo.value.areaSquareMeters = 0
		drawInfo.value.distanceMeters = 0
		if (drawInfoBox?.value) {
			drawInfoBox.value.hidden = true
		}
		isDrawing = false
	})

	map.on("click", (event: CustomMapMouseEvent) => {
		const mode = draw.getMode()
		const isMeasuring =
			isDrawing ||
			mode === MapboxDraw.constants.modes.DRAW_POLYGON ||
			mode === MapboxDraw.constants.modes.DRAW_LINE_STRING ||
			performance.now() - measureCompletedAt < 400

		if (isMeasuring) {
			if (event.stopMapPropagation) {
				return
			}
			event.stopMapPropagation = true
		}
	})

	// For debugging, enabling these will provide useful visual and console feedback
	/* map.showPadding = true */
	/* map.showTileBoundaries = true; */
	/* map.on('zoom', () => { */
	/* 	console.log(map.getZoom().toFixed(2)) */
	/* }); */

	window.map = map
	window.store = store
	mapRef.value = map
	mapCenter.value = map.getCenter()
	map.on("move", pulseMoveHandler)

	mapBearing.value = map.getBearing()
	mapPitch.value = map.getPitch()
	map.on("rotate", () => {
		mapBearing.value = map.getBearing()
	})

	map.on("styledata", () => {
		if (!map.isStyleLoaded()) return
		mapStyleReadyTick.value++

		const style = map.getStyle()
		if (!style?.layers) return

		store.setLayers(
			style.layers
				.filter(isCustomLayer)
				.map(layer => layer.id)
				.filter(layerId => !OPPORTUNITY_RENDER_LAYER_IDS.includes(layerId))
		)
	})

	map.on("load", async () => {
		emit("loaded", true)
		resumePendingTargetSyncFromStorage()
	})

	let defaultTerrain: mapboxgl.TerrainSpecification | undefined = undefined
	let currentStyleRequestId = 0
	let latestStyleLoadRunId = 0
	let styleWatchersInitialized = false
	let hasRenderedOpportunitiesOnce = false

	map.on("style.load", async () => {
		const styleRequestId = currentStyleRequestId
		const styleLoadRunId = ++latestStyleLoadRunId
		const isCurrentStyle = () => styleRequestId === currentStyleRequestId && styleLoadRunId === latestStyleLoadRunId

		// Base style configuration
		{
			if (usesStandardStyle(map)) {
				// NOTE these could instead be exposed as layers when
				// Mapbox Standard basemap is active
				map.setConfigProperty("basemap", "showRoadLabels", true)
				map.setConfigProperty("basemap", "showPlaceLabels", true)
				map.setConfigProperty("basemap", "showPointOfInterestLabels", true)
				map.setConfigProperty("basemap", "showTransitLabels", true)
				map.setConfigProperty("basemap", "lightPreset", "day,") // dusk, dawn, day, night
			}

			if (usesStandardStyle(map) || usesSatelliteStyle(map)) {
				map.addSource("mapbox-dem", {
					type: "raster-dem",
					url: "mapbox://mapbox.mapbox-terrain-dem-v1",
				})
				map.setTerrain({ source: "mapbox-dem" })
			}

			defaultTerrain = map.getTerrain() ?? undefined
		}

		await Promise.all([loadHatchPatterns(map, isCurrentStyle), loadOpportunityIcons(map, isCurrentStyle)])
		if (!isCurrentStyle()) return

		// Add visual data layers, as mapbox sources and layers
		// prettier-ignore
		{
			addParcels(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				sourceName:    MapSourceId.Cadastre,
				layerName:     "cadastre",
				theme:         isSatelliteStyle(currentStyle.value) ? ParcelsTheme.Satellite : ParcelsTheme.Vector,
			})
			applyCadastreViewFilter(map)
			restoreSelectedParcelFeatureStates(map)
			highlightOpportunityParcels(map)
			addRegions(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "administrative-divisions/regions",
				sourceName:    "administrative-divisions/regions-source",
			})
			addRegionLabels(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "administrative-divisions/region_labels",
				sourceName:    "administrative-divisions/region_labels-source",
			})
			addMRC(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "administrative-divisions/rcms",
				sourceName:    "administrative-divisions/rcms-source",
				isSatellite:   isSatelliteStyle(currentStyle.value),
			})
			addMRCLabels(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "administrative-divisions/rcm_labels",
				sourceName:    "administrative-divisions/rcm_labels-source",
			})
			addMetropolitanAreas(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "administrative-divisions/metropolitan-areas",
				sourceName:    "administrative-divisions/metropolitan-areas-source",
			})
			addMunicipalities(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "administrative-divisions/municipalities",
				sourceName:    "administrative-divisions/municipalities-source",
				isSatellite:   isSatelliteStyle(currentStyle.value),
			})
			addMunicipalityLabels(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "administrative-divisions/municipality_labels",
				sourceName:    "administrative-divisions/municipality_labels-source",
			})
			addBorougs(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "administrative-divisions/boroughs",
				sourceName:    "administrative-divisions/boroughs-source",
			})
			addPostalCodes(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "administrative-divisions/postal-codes",
				sourceName:    "administrative-divisions/postal-codes-source",
			})
			addPostalCodeLabels(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "administrative-divisions/postal-code_labels",
				sourceName:    "administrative-divisions/postal-code_labels-source",
			})
			addUrbanPlanning(map, {
				baseUrl:       TILES_URL_LIVE,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "urban-planning",
				sourceName:    "urban-planning-source",
			})
			addPlanningPolicy(map, {
				baseUrl:       TILES_URL_LIVE,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "planning-policy",
				sourceName:    "planning-policy-source",
			})
			addUrbanPerimeters(map, {
				baseUrl:       TILES_URL_LIVE,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "urban-perimeters",
				sourceName:    "urban-perimeters-source",
				beforeLayerName: "administrative-divisions/regions",
			})
			if (store.currentUser?.has(Permission.ZoningRead)) {
				addZoning(map, {
					baseUrl:       TILES_URL_LIVE,
					visibleLayers: toRaw(visibleLayers.value),
					beforeLayerName: "administrative-divisions/regions",
					isSatellite:   isSatelliteStyle(currentStyle.value),
				})
			}
			addLandUse(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "land-use",
				sourceName:    "land-use-source",
			})
			addAgriculturalZones(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "agricultural-zones",
				sourceName:    "agricultural-zones-source",
				isSatellite:   isSatelliteStyle(currentStyle.value),
			})
			addFloodableZones(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "floodable-zones",
				sourceName:    "floodable-zones-source",
			})
			addWetlands(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "wetlands",
				sourceName:    "wetlands-source",
				isSatellite:   isSatelliteStyle(currentStyle.value),
			})
			addEndangeredSpecies(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				sourceName:    "endangered-species-source",
				enabledSpecies: filters.value.endangeredSpecies,
				locale:        currentLocale.value,
			})
			addAgriculturalInclusionsExclusions(map, {
				baseUrl:       TILES_URL_LIVE,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "agricultural-inclusions-exclusions",
				sourceName:    "agricultural-inclusions-exclusions-source",
			})
			addSoilCapabilityForAgricultureFill(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "soil-capability",
				sourceName:    "soil-capability-source",
			})
			addSoilCapabilityForAgricultureLine(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "soil-capability_line",
				sourceName:    "soil-capability_line-source",
			})
			addSoilCapabilityForAgricultureLabels(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "soil-capability_labels",
				sourceName:    "soil-capability_labels-source",
			})
			addTransitLines(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "transit-lines",
				sourceName:    "transit-lines-source",
			})
			addTransitStations(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "transit-stations",
				sourceName:    "transit-stations-source",
			})
			addTransitStationsLabels(map, {
				baseUrl:       TILES_URL_DAILY,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "transit-stations_labels",
				sourceName:    "transit-stations_labels-source",
			})
			addProspects(map, {
				baseUrl:       TILES_URL_LIVE,
				visibleLayers: toRaw(visibleLayers.value),
				layerName:     "prospects",
				sourceName:    MapSourceId.Prospects,
			})

      if (store.currentUser?.has(Permission.UrbanPrioritiesRead)) {
				addUrbanPriorities(map, {
					baseUrl:       TILES_URL_LIVE,
					visibleLayers: toRaw(visibleLayers.value),
					layerName:     "urban-priorities",
					sourceName:    "urban-priorities-source",
				})
			}

			if (store.currentUser?.has(Permission.CityPvDocumentsRead)) {
				addCityPvDocuments(map, TILES_URL_DAILY, toRaw(visibleLayers.value))
			}

			if (store.currentUser?.has(Permission.TransactionRead) && config.ui.has(Feature.Transactions)) {
				addTransactions(map, {
					baseUrl:       TILES_URL_LIVE,
					visibleLayers: toRaw(visibleLayers.value),
					layerName:     "transaction-fill",
					sourceName:    MapSourceId.Transactions,
				})
				applyActiveTransactionFilters(map, selectedTransaction.value)
				applyTransactionFiltersWhenSourceLoads(map, selectedTransaction.value)
			}

			if (store.currentUser?.has(Permission.CompletedAssessmentsRead)) {
				addCompletedAssessments(map, {
					baseUrl: TILES_URL_LIVE,
					visibleLayers: toRaw(visibleLayers.value),
					layerName: LayerType.CompletedAssessments,
					sourceName: MapSourceId.CompletedAssessments,
				})
			}

			addMunicipalityFocus(map, "administrative-divisions/municipalities-source")
			applyMunicipalityFocus(map, props.focusedMunicipalityCode)
		}

		// Ensure default layers are set for anonymous users (no-op for CCA projects)
		store.initializeDefaultLayers()

		if (!hasRenderedOpportunitiesOnce) {
			isLoadingInitialOpportunities.value = true
			setTimeout(() => (isLoadingInitialOpportunities.value = false), 10000)
		}
		if (isHuntingMode() && currentProject.value == null) {
			await waitForCurrentProject()
		}

		const project = currentProject.value
			? currentProject
			: await projectStore.fetchCurrentProjectMetadata({ includePeople: false })
		if (!isCurrentStyle()) {
			isLoadingInitialOpportunities.value = false
			return
		}

		if (project.value?.type !== ProjectType.Workspace && project.value?.searchFilter) {
			store.setFilters(project.value.searchFilter)
		}
		if (store.searches.opportunity) {
			void handleSearchByOpportunityId(store.searches.opportunity)
		}

		refreshOpportunityVectorTiles(map)
		hasRenderedOpportunitiesOnce = true
		isLoadingInitialOpportunities.value = false

		if (styleWatchersInitialized) return

		if (!isCurrentStyle() || styleWatchersInitialized) return
		styleWatchersInitialized = true

		watch(
			filters,
			() => {
				setOpportunityLayersVisibility(map, opportunityLayerVisibility())
				applyOpportunityTypeFilters(map)
				refreshOpportunityVectorTiles(map)
			},
			{ deep: true, immediate: true }
		)
		watch(
			() => scopedOpportunityProject()?.id ?? null,
			() => {
				refreshOpportunityVectorTiles(map)
			}
		)
		watch(
			() => store.opportunityTileRefreshKey,
			() => {
				refreshOpportunityVectorTiles(map)
			}
		)
		watch(
			() => activeOpportunityView.value.view.value,
			() => {
				applyOpportunityTypeFilters(map)
				refreshOpportunityVectorTiles(map)
			},
			{ deep: true }
		)
		watch(activeOpportunityMapTypes, () => {
			applyOpportunityTypeFilters(map)
			refreshOpportunityVectorTiles(map)
		})
		watch(
			() => currentProject.value?.id,
			(projectId, previousProjectId) => {
				if (!isHuntingMode() || projectId === previousProjectId) return
				refreshOpportunityVectorTiles(map)
			}
		)
		watch(
			() => activeParcelView.value.view.value,
			() => applyCadastreViewFilter(map),
			{ deep: true }
		)
		watch(
			() => activeTransactionView.value.view.value,
			() => applyActiveTransactionFilters(map, selectedTransaction.value),
			{ deep: true }
		)
		watch(
			filters,
			async () => {
				applyTransactionViewFilter(map, selectedTransaction.value)
				applyTransactionFiltersWhenSourceLoads(map, selectedTransaction.value)
			},
			{ deep: true, immediate: true }
		)
		watch(
			filters,
			() => {
				const conditions: any[] = []

				if (filters.value.urbanPriorities.pmad) {
					conditions.push(["==", ["get", "pmad_active"], true])
					conditions.push(["==", ["get", "pmad_draft"], true])
				}

				if (filters.value.urbanPriorities.sad) {
					conditions.push(["==", ["get", "sad_active"], true])
					conditions.push(["==", ["get", "sad_draft"], true])
				}

				if (filters.value.urbanPriorities.pu) {
					conditions.push(["==", ["get", "pu_active"], true])
					conditions.push(["==", ["get", "pu_draft"], true])
				}

				if (filters.value.urbanPriorities.ppu) {
					conditions.push(["==", ["get", "ppu_active"], true])
					conditions.push(["==", ["get", "ppu_draft"], true])
				}

				if (filters.value.urbanPriorities.pu_interview) {
					conditions.push(["==", ["get", "pu_active_interview"], true])
					conditions.push(["==", ["get", "pu_draft_interview"], true])
				}

				if (filters.value.urbanPriorities.other) {
					conditions.push(["==", ["get", "other_active"], true])
					conditions.push(["==", ["get", "other_draft"], true])
				}

				// Fallback to show nothing if no filters are active
				if (conditions.length === 0) {
					conditions.push(["==", ["get", "pmad_active"], "__none__"])
				}

				const filter: mapboxgl.FilterSpecification = ["any", ...conditions]

				store.currentUser.has(Permission.UrbanPrioritiesRead) && map.setFilter("urban-priorities", filter)
				store.currentUser.has(Permission.UrbanPrioritiesRead) &&
					map.setPaintProperty("urban-priorities", "fill-color", getUrbanPriorityColorExpression(filters.value))
				store.currentUser.has(Permission.UrbanPrioritiesRead) &&
					map.setPaintProperty("urban-priorities", "fill-opacity", getUrbanPriorityOpacityExpression(filters.value))
			},
			{ deep: true, immediate: true }
		)
		watch(
			filters,
			async () => {
				const typeConditions: any[] = []

				if (filters.value.planningPolicy.tod) {
					typeConditions.push(["==", ["get", "type"], "TOD"])
				}

				if (filters.value.planningPolicy.ppu) {
					typeConditions.push(["==", ["get", "type"], "PPU"])
				}

				if (filters.value.planningPolicy.corridor) {
					typeConditions.push(["==", ["get", "type"], "Corridor de transport métropolitain"])
				}

				if (filters.value.planningPolicy.strategicSector) {
					typeConditions.push(["==", ["get", "type"], "Secteur de planification stratégique"])
					typeConditions.push(["==", ["get", "type"], "SPI"])
				}

				if (typeConditions.length === 0) {
					typeConditions.push(["==", ["get", "type"], "__none__"])
				}

				const typeFilter: mapboxgl.FilterSpecification = ["any", ...typeConditions]
				const solidFilter: mapboxgl.FilterSpecification = ["all", ["==", ["get", "source"], "PMADR"], typeFilter]
				const hatchFilter: mapboxgl.FilterSpecification = ["all", ["==", ["get", "source"], "PU"], typeFilter]
				const lineFilter: mapboxgl.FilterSpecification = ["all", ["==", ["get", "source"], "SAD"], typeFilter]

				if (map.getLayer("planning-policy")) {
					map.setFilter("planning-policy", solidFilter)
				}
				if (map.getLayer("planning-policy-hatch")) {
					map.setFilter("planning-policy-hatch", hatchFilter)
				}
				if (map.getLayer("planning-policy-line")) {
					map.setFilter("planning-policy-line", lineFilter)
				}
				if (map.getLayer("planning-policy-hit")) {
					map.setFilter("planning-policy-hit", typeFilter)
				}
			},
			{ deep: true, immediate: true }
		)
		watch(
			filters,
			async () => {
				for (const definition of endangeredSpeciesLayerDefinitions) {
					const animalFilter = filters.value.endangeredSpecies[definition.filterKey]
						? getEndangeredSpeciesFilter(definition.classes, "Animaux")
						: getDisabledEndangeredSpeciesFilter()
					const plantFilter = filters.value.endangeredSpecies[definition.filterKey]
						? getEndangeredSpeciesFilter(definition.classes, "Plantes")
						: getDisabledEndangeredSpeciesFilter()
					const outlineFilter = filters.value.endangeredSpecies[definition.filterKey]
						? getEndangeredSpeciesFilter(definition.classes)
						: getDisabledEndangeredSpeciesFilter()

					if (map.getLayer(definition.id)) {
						map.setFilter(definition.id, animalFilter)
					}
					if (map.getLayer(`${definition.id}-plants`)) {
						map.setFilter(`${definition.id}-plants`, plantFilter)
					}
					if (map.getLayer(`${definition.id}-line`)) {
						map.setFilter(`${definition.id}-line`, outlineFilter)
					}
				}
				if (map.getLayer("endangered-species-labels")) {
					map.setFilter("endangered-species-labels", getActiveEndangeredSpeciesFilter(filters.value.endangeredSpecies))
				}
			},
			{ deep: true, immediate: true }
		)
		watch(currentLocale, () => {
			if (map.getLayer("endangered-species-labels")) {
				map.setLayoutProperty(
					"endangered-species-labels",
					"text-field",
					getEndangeredSpeciesLabelField(currentLocale.value)
				)
			}
		})
		refreshOpportunityVectorTiles(map)
		watch(
			filters,
			() => {
				if (map.getLayer("prospects")) {
					map.setPaintProperty("prospects", "fill-opacity", getProspectOpacityExpression(filters.value))
				}
			},
			{ deep: true, immediate: true }
		)
		watch(
			filters,
			() => {
				const lineStatusConditions: any[] = []
				if (filters.value.transitLinesStatus.inUse) {
					lineStatusConditions.push(["==", ["get", "status"], "in_use"])
				}
				if (filters.value.transitLinesStatus.inConstruction) {
					lineStatusConditions.push(["==", ["get", "status"], "in_construction"])
				}
				if (filters.value.transitLinesStatus.proposed) {
					lineStatusConditions.push(["==", ["get", "status"], "proposed"])
				}
				if (lineStatusConditions.length === 0) {
					lineStatusConditions.push(["==", ["get", "status"], "__none__"])
				}
				const lineFilter: mapboxgl.FilterSpecification = ["any", ...lineStatusConditions]

				const stationStatusConditions: any[] = []
				if (filters.value.transitStationsStatus.inUse) {
					stationStatusConditions.push(["==", ["get", "status"], "in_use"])
				}
				if (filters.value.transitStationsStatus.inConstruction) {
					stationStatusConditions.push(["==", ["get", "status"], "in_construction"])
				}
				if (filters.value.transitStationsStatus.proposed) {
					stationStatusConditions.push(["==", ["get", "status"], "proposed"])
				}
				if (stationStatusConditions.length === 0) {
					stationStatusConditions.push(["==", ["get", "status"], "__none__"])
				}
				const stationFilter: mapboxgl.FilterSpecification = ["any", ...stationStatusConditions]

				if (map.getLayer("transit-lines")) {
					map.setFilter("transit-lines", lineFilter)
				}
				if (map.getLayer("transit-stations")) {
					map.setFilter("transit-stations", stationFilter)
				}
				if (map.getLayer("transit-stations_labels")) {
					map.setFilter("transit-stations_labels", stationFilter)
				}
			},
			{ deep: true, immediate: true }
		)
	})

	map.on("pitch", () => {
		mapPitch.value = map.getPitch()
		if (map.getPitch() === 0) {
			// This is a fix to get layers to obey "slot: 'top'".
			// see: https://github.com/mapbox/mapbox-gl-js/issues/13036#issuecomment-1883702763
			map.setTerrain(undefined)
		} else {
			map.setTerrain(defaultTerrain)
		}
	})

	map.on("moveend", () => {
		const cameraPosition = window.location.hash.replace("#", "") as MapboxCameraPositionHash
		store.setCameraPosition(cameraPosition)

		mapCenter.value = map.getCenter()
		void emitViewportOpportunities()
	})
	map.on("zoomend", () => refreshOpportunityVectorTiles(map))
	map.on("idle", () => void emitViewportOpportunities())

	// Display appropriate cursor over icons
	{
		const layers = [...OPPORTUNITY_INTERACTIVE_LAYER_IDS, "transaction-icons"]
		map.on("mouseenter", layers, () => {
			map.getCanvas().style.cursor = "pointer"
		})
		map.on("mouseleave", layers, () => {
			map.getCanvas().style.cursor = ""
		})
	}

	// Handle mouse hover over transaction area (preview)
	if (store.currentUser?.has(Permission.TransactionRead) && config.ui.has(Feature.Transactions)) {
		map.on("mouseenter", "transaction-fill", async event => {
			if (!event.features || event.features.length === 0) return
			previewedTransaction.value = event.features[0].id
		})
		map.on("mouseleave", "transaction-fill", () => {
			previewedTransaction.value = null
		})
	}

	// NOTE the order of click handlers matters; they will be called in
	// the order they are defined.
	// opportunities should be handled first
	const handleOpportunityClusterClick = (event: CustomMapMouseEvent) => {
		if (event.stopMapPropagation) return
		event.stopMapPropagation = true

		const feature = event.features?.[0]
		const coordinates = (feature?.geometry as { coordinates?: [number, number] } | undefined)?.coordinates
		if (!coordinates) return

		map.easeTo({
			center: coordinates,
			zoom: Math.min(map.getZoom() + 2, 15),
			duration: 550,
		})
	}

	for (const clusterLayerId of [OPPORTUNITY_CLUSTER_LAYER_ID, OPPORTUNITY_CLUSTER_COUNT_LAYER_ID]) {
		onMapClick(map, clusterLayerId, handleOpportunityClusterClick)
		map.on("mouseenter", clusterLayerId, () => {
			map.getCanvas().style.cursor = "pointer"
		})
		map.on("mouseleave", clusterLayerId, () => {
			map.getCanvas().style.cursor = ""
		})
	}

	const handleOpportunityClick = async (event: any) => {
		if (event.stopMapPropagation) return

		event.stopMapPropagation = true
		setFeatureDetails([])
		const feature = event.features?.[0]
		const opportunityId = feature ? String(feature.properties?.id ?? feature.id ?? "") : ""
		if (!opportunityId) return
		if (isDuplicateOpportunityClick(opportunityId, event)) return

		if (isPendingOpportunityTileFeature(feature)) {
			clearSelections()
			emit("error", "search.opportunitySyncPending", { params: { id: opportunityId } })
			return
		}

		const prevCursor = map.getCanvas().style.cursor
		map.getCanvas().style.cursor = "wait"
		try {
			const opportunity = await handleSearchByOpportunityId(opportunityId, { navigate: false })
			if (opportunity) {
				console.info("Opportunity vector tile clicked and fetched.", {
					id: opportunity.id,
					hubspotRecordId: opportunity.hubspot_record_id,
					type: opportunity.type,
					source: "landerz_models.opportunity_view",
				})
			}
		} finally {
			map.getCanvas().style.cursor = prevCursor
		}
	}
	for (const opportunityLayerId of OPPORTUNITY_INTERACTIVE_LAYER_IDS) {
		onMapClick(map, opportunityLayerId, handleOpportunityClick)
	}

	onMapClick(map, "transaction-icons", (event: CustomMapMouseEvent) => {
		if (event.stopMapPropagation) {
			return
		}
		event.stopMapPropagation = true
		setFeatureDetails([])

		// update transaction
		if (!event.features || event.features.length === 0) return

		const transaction = toRaw(store.getTransactionById(event.features[0].id))
		if (transaction) {
			exitSelectionMode()
			store.updateSelectedTransaction(transaction)
		}
	})

	const handleTransactionBasedLayerClick = async (event: CustomMapMouseEvent) => {
		if (event.stopMapPropagation) return
		event.stopMapPropagation = true
		setFeatureDetails([])
		preventFlyTo = true

		if (!event.features || event.features.length === 0) return
		const inscriptionNumber = event.features[0].properties?.inscription_number
		if (typeof inscriptionNumber !== "string" || inscriptionNumber.length === 0) return

		const isSelectionClick = isCtrlPressed.value
		const prevCursor = map.getCanvas().style.cursor
		map.getCanvas().style.cursor = "wait"
		store.isSelectedTransactionLoading = true
		const transaction = await store.getTransactionByInscriptionNumber(inscriptionNumber)
		map.getCanvas().style.cursor = prevCursor

		if (transaction) {
			exitSelectionMode()

			if (isSelectionClick) {
				const transactionId = transaction.presentationId
				if (transactionId != null) {
					if (store.transactionExportList.has(transactionId)) {
						store.transactionExportList.delete(transactionId)
					} else {
						store.transactionExportList.set(transactionId, transaction)
					}
				}
				store.addSelectedTransaction(transaction)
				store.isSelectedTransactionLoading = false
				return
			}

			store.updateSelectedTransaction(transaction)
		}

		store.isSelectedTransactionLoading = false
	}

	onMapClick(map, "transaction-fill", async (event: CustomMapMouseEvent) => {
		await handleTransactionBasedLayerClick(event)
	})

	onMapClick(map, "city-pv-documents", (event: CustomMapMouseEvent) => {
		if (event.stopMapPropagation) return
		event.stopMapPropagation = true

		const features = map.queryRenderedFeatures(event.point, { layers: ["city-pv-documents"] })
		const seen = new Set<string>()
		const details = features.flatMap(feature => {
			const key = String(feature.properties?.city_pv_document_id ?? feature.id ?? feature.properties?.segment_id ?? "")
			if (seen.has(key)) return []
			seen.add(key)
			return [buildCityPvDocumentDetail(feature)]
		})

		exitSelectionMode()
		selectedTransaction.value = null
		setFeatureDetails(details)
		pulse.lngLat = { lng: event.lngLat.lng, lat: event.lngLat.lat }
		pulse.point = { x: event.point.x, y: event.point.y }
		pulse.visible = details.length > 0
		pulse.key += 1
	})

	map.on("mouseenter", "city-pv-documents", () => {
		map.getCanvas().style.cursor = "pointer"
	})
	map.on("mouseleave", "city-pv-documents", () => {
		map.getCanvas().style.cursor = ""
	})

	if (store.currentUser?.has(Permission.CompletedAssessmentsRead)) {
		map.on("mousemove", LayerType.CompletedAssessments, event => {
			const feature = event.features?.[0]
			const id = feature?.id ?? feature?.properties?.hubspot_id
			if (id == null || id === hoveredCompletedAssessmentFeatureId) {
				return
			}

			if (hoveredCompletedAssessmentFeatureId !== null) {
				setCompletedAssessmentFeatureState(map, hoveredCompletedAssessmentFeatureId, { hover: false })
			}

			hoveredCompletedAssessmentFeatureId = id
			setCompletedAssessmentFeatureState(map, id, { hover: true })
			map.getCanvas().style.cursor = "pointer"
		})
		map.on("mouseleave", LayerType.CompletedAssessments, () => {
			if (hoveredCompletedAssessmentFeatureId !== null) {
				setCompletedAssessmentFeatureState(map, hoveredCompletedAssessmentFeatureId, { hover: false })
				hoveredCompletedAssessmentFeatureId = null
			}

			map.getCanvas().style.cursor = ""
		})

		onMapClick(map, LayerType.CompletedAssessments, event => {
			const customEvent = event as CustomMapMouseEvent
			if (customEvent.stopMapPropagation) {
				return
			}

			customEvent.stopMapPropagation = true
			const feature = customEvent.features?.[0]
			if (!feature) {
				return
			}

			clearHighlights(map)
			clearSelections()
			selectedTransaction.value = null
			setFeatureDetails([buildCompletedAssessmentDetail(feature)])
		})
	}

	map.on("mousemove", "zoning", event => {
		const feature = event.features?.[0]
		const id = feature?.id ?? feature?.properties?.zone_code ?? null
		setHoveredZoningFeature(map, id)
		map.getCanvas().style.cursor = id === null ? "" : "pointer"
	})
	map.on("mouseleave", "zoning", () => {
		setHoveredZoningFeature(map, null)
		map.getCanvas().style.cursor = ""
	})

	map.on("mousemove", "cadastre", event => {
		if (!event.features || event.features.length === 0) return
		const feature = event.features[0]
		if (feature.id === undefined || feature.id === hoveredParcelFeatureId) return

		if (hoveredParcelFeatureId !== null) {
			map.setFeatureState(
				{ source: MapSourceId.Cadastre, sourceLayer: PARCEL_SOURCE_LAYER, id: hoveredParcelFeatureId },
				{ hover: false }
			)
		}
		hoveredParcelFeatureId = feature.id
		map.setFeatureState(
			{ source: MapSourceId.Cadastre, sourceLayer: PARCEL_SOURCE_LAYER, id: hoveredParcelFeatureId },
			{ hover: true }
		)
	})
	map.on("mouseleave", "cadastre", () => {
		if (hoveredParcelFeatureId !== null) {
			map.setFeatureState(
				{ source: MapSourceId.Cadastre, sourceLayer: PARCEL_SOURCE_LAYER, id: hoveredParcelFeatureId },
				{ hover: false }
			)
			hoveredParcelFeatureId = null
		}
	})

	onMapClick(map, "cadastre", (event: any) => {
		if (event.stopMapPropagation) {
			return
		}
		event.stopMapPropagation = true
		setFeatureDetails([])

		if (longPressFired) {
			longPressFired = false
			return
		}

		if (!isCtrlPressed.value && !selectionModeActive.value) {
			selectParcel(map, event)
		} else {
			toggleParcelSelection(map, event)
		}
	})

	// Fallback click handler for clearing selections/highlights & feature details
	onMapClick(map, null, (event: any) => {
		if (event.stopMapPropagation) return

		event.stopMapPropagation = true

		const cadastreVisible = isLayerVisible(map, "cadastre")
		const details = cadastreVisible ? [] : collectIntersectDetails(map, event.point)
		const zoningFeature =
			!cadastreVisible && isLayerVisible(map, "zoning")
				? map.queryRenderedFeatures(event.point, { layers: ["zoning"] })[0]
				: undefined
		setFeatureDetails(details)
		setSelectedZoningFeature(map, zoningFeature)

		if (!cadastreVisible && details.length > 0) {
			pulse.lngLat = { lng: event.lngLat.lng, lat: event.lngLat.lat }
			pulse.point = { x: event.point.x, y: event.point.y }
			pulse.visible = true
			pulse.key += 1
		} else {
			clearPulse()
		}

		// If Ctrl is pressed or touch selection mode is active, do not clear selection
		if (isCtrlPressed.value || selectionModeActive.value) {
			return
		}

		// Otherwise, clear all selections
		marker.remove()
		clearHighlights(map)
		clearSelections()
		selectedTransaction.value = null
	})

	// Highlight transaction when previewed
	watch(previewedTransaction, (curr, prev) => {
		if (prev) {
			setTransactionFeatureState(map, prev, { preview: false })
			map.getCanvas().style.cursor = ""
		}
		if (curr) {
			setTransactionFeatureState(map, curr, { preview: true })
			map.getCanvas().style.cursor = "pointer"
		}
	})

	watch(selectedTransaction, async (curr, prev) => {
		if (curr) {
			store.ensureLayerSelected(LayerType.Transaction)
		}

		applyActiveTransactionFilters(map, curr)
		applyTransactionFiltersWhenSourceLoads(map, curr)

		if (prev?.presentationId != null) {
			setTransactionFeatureState(map, Number(prev.presentationId), { highlight: false })
		}
		if (curr) {
			const { longitude, latitude } = curr.location?.coordinates ?? {}

			if (!preventFlyTo && longitude != null && latitude != null) {
				map.once("moveend", () => {
					if (curr.presentationId != null) {
						setTransactionFeatureState(map, Number(curr.presentationId), { highlight: true })
					}
				})

				map.flyTo({
					center: new mapboxgl.LngLat(Number(longitude), Number(latitude)),
					zoom: 15,
					speed: 1.6,
				})
			} else {
				if (curr.presentationId != null) {
					setTransactionFeatureState(map, Number(curr.presentationId), { highlight: true })
				}
			}
		}
		preventFlyTo = false
	})

	watch(currentStyle, name => {
		currentStyleRequestId++
		clearPendingCadastreSelectionRestore(map)
		map.setStyle(BASE_MAP_STYLES[name], { diff: false } as any)
		selectedFeatureId.value = null
	})

	watch(
		() => store.modifiedTransaction,
		() => {
			refreshSource(map, MapSourceId.Transactions)
		},
		{ deep: true }
	)
	watch(
		prospects,
		() => {
			setProspectFeatureStates(map)
		},
		{ deep: true, immediate: true }
	)
	watch(
		urbanPriorities,
		() => {
			refreshSource(map, "urban-priorities-source")
		},
		{ deep: true }
	)
	// prettier-ignore
	watch(hoveredOpportunityId, (newVal, oldVal) => {
			if (oldVal) {
				setOpportunityFeatureState(map, oldVal, { highlight: false })
			}
			if (newVal) {
				setOpportunityFeatureState(map, newVal, { highlight: true })
			}
			if (selectedOpportunityId.value) {
				setOpportunityFeatureState(map, selectedOpportunityId.value, { highlight: true })
			}
		})

	// Set map padding, which alters what is considered center for
	// certain functions, like flyTo and fitBounds.
	// see https://docs.mapbox.com/mapbox-gl-js/api/properties/#paddingoptions
	watch(
		isLargeScreen,
		isLarge => {
			if (isLarge) {
				map.setPadding({
					top: 0,
					bottom: 0,
					left: 468,
					right: 1019,
				})
			} else {
				map.setPadding({
					top: 0,
					bottom: 0,
					left: 0,
					right: 0,
				})
			}
		},
		{ immediate: true }
	)

	watch(selectedOpportunityId, (newSelectedId, oldSelectedId) => {
		highlightOpportunityParcels(map)
		applySelectedOpportunityIconSize(map, newSelectedId)
		applySelectedOpportunityIconOpacity(map, newSelectedId)

		// TODO what does this do in practice? what gets highlighted exactly?
		// TODO the conditional logic doesn't seem right; double check
		if (oldSelectedId !== null) {
			setOpportunityFeatureState(map, oldSelectedId, { highlight: false })
		}
		if (newSelectedId !== null) {
			setOpportunityFeatureState(map, newSelectedId, { highlight: true })
		}
	})

	// NOTE:
	// Ideally codependent source layers would be included in the same
	// tile source, as two items in the "tiles" array
	// (`map.addSource("name", {tiles: [...]})`). Unfortunately this
	// produces a mapbox-gl glitch where the layer filling is
	// inconsistant and changes based on zoom level. Bumbing mapbox-gl
	// to the latest version (3.5.1) did not help.
	//
	// TODO:
	// Combine codependent source layers in a single db view so that
	// they become a single data source. This would eleminate the below
	// (error prone) logic and possibly improve performance.
	function setLayerVisibility(layerId: string, visibility: "visible" | "none"): void {
		if (!map.getLayer(layerId)) return
		map.setLayoutProperty(layerId, "visibility", visibility)
	}

	function setLayerAndCodependencyVisibility(layerId: string, visibility: "visible" | "none"): void {
		setLayerVisibility(layerId, visibility)

		// when a layer is toggled, so should its codependencies
		switch (layerId) {
			case ParcelsLayerId.Fill: {
				setLayerVisibility(ParcelsLayerId.Line, visibility)
				break
			}
			case "administrative-divisions/regions": {
				setLayerVisibility("administrative-divisions/regions_line", visibility)
				setLayerVisibility("administrative-divisions/region_labels", visibility)
				break
			}
			case "administrative-divisions/rcms": {
				setLayerVisibility("administrative-divisions/rcms_line", visibility)
				setLayerVisibility("administrative-divisions/rcm_labels", visibility)
				break
			}
			case MapLayerId.MetropolitanAreas: {
				setLayerVisibility("administrative-divisions/metropolitan-areas_line", visibility)
				break
			}
			case MapLayerId.Boroughs: {
				setLayerVisibility("administrative-divisions/boroughs_line", visibility)
				break
			}
			case "administrative-divisions/municipalities": {
				setLayerVisibility("administrative-divisions/municipality_labels", visibility)
				setBaseMapSettlementLabelsYieldToMunicipalities(map, visibility === "visible")
				break
			}
			case "administrative-divisions/postal-codes": {
				setLayerVisibility("administrative-divisions/postal-code_labels", visibility)
				break
			}
			case MapLayerId.AgriculturalZones: {
				setLayerVisibility("agricultural-zones_line", visibility)
				break
			}
			case MapLayerId.Wetlands: {
				setLayerVisibility("wetlands_line", visibility)
				break
			}
			case "planning-policy": {
				setLayerVisibility("planning-policy-hatch", visibility)
				setLayerVisibility("planning-policy-line", visibility)
				setLayerVisibility("planning-policy-hit", visibility)
				break
			}
			case "soil-capability": {
				setLayerVisibility("soil-capability_line", visibility)
				setLayerVisibility("soil-capability_labels", visibility)
				break
			}
			case "endangered-species": {
				for (const definition of endangeredSpeciesLayerDefinitions) {
					setLayerVisibility(definition.id, visibility)
					setLayerVisibility(`${definition.id}-plants`, visibility)
					setLayerVisibility(`${definition.id}-line`, visibility)
				}
				setLayerVisibility("endangered-species-labels", visibility)
				break
			}
			case "transit-stations": {
				setLayerVisibility("transit-stations_labels", visibility)
				break
			}
			case "zoning": {
				if (visibility === "none") {
					setHoveredZoningFeature(map, null)
					setSelectedZoningFeature(map)
				}
				setLayerVisibility("zoning-outline", visibility)
				setLayerVisibility("zoning-labels", visibility)
				break
			}
			case "city-pv-documents": {
				setLayerVisibility("city-pv-documents-halo", visibility)
				break
			}
			case LayerType.CompletedAssessments: {
				if (visibility === "none" && hoveredCompletedAssessmentFeatureId !== null) {
					setCompletedAssessmentFeatureState(map, hoveredCompletedAssessmentFeatureId, { hover: false })
					hoveredCompletedAssessmentFeatureId = null
				}
				setLayerVisibility(`${LayerType.CompletedAssessments}-halo`, visibility)

				break
			}
		}
	}

	watch(
		[visibleLayers, mapStyleReadyTick],
		([newVal, styleReadyTick], [oldVal = [], previousStyleReadyTick] = [[], styleReadyTick]) => {
			const previousVisibleLayers = styleReadyTick === previousStyleReadyTick ? oldVal : []
			const visibleLayerSet = new Set(newVal)
			const layerIdsToReconcile = new Set([
				...layers.value,
				...previousVisibleLayers,
				...visibleLayers.value,
				...newVal,
			])

			layerIdsToReconcile.forEach(layerId => {
				const isEndangeredSpeciesCodependency = endangeredSpeciesLayerDefinitions.some(
					definition => layerId === `${definition.id}-plants` || layerId === `${definition.id}-line`
				)
				if (CODEPENDENT_LAYER_IDS.has(layerId) || isEndangeredSpeciesCodependency) {
					return
				}
				setLayerAndCodependencyVisibility(layerId, visibleLayerSet.has(layerId) ? "visible" : "none")
			})

			if (newVal.includes("cadastre")) {
				setSelectedZoningFeature(map)
				clearPulse()
				emit("layer-features", [])
			}
		},
		{ immediate: true }
	)

	watch(
		selectedParcelIDs,
		ids => {
			if (ids.length === 0) selectionModeActive.value = false

			totalSqFt.value = 0
			totalSqMeter.value = 0
			const map = mapRef.value
			if (!map) return

			const features = map.querySourceFeatures(MapSourceId.Cadastre, {
				sourceLayer: PARCEL_SOURCE_LAYER,
			})

			for (const id of selectedParcelIDs.value) {
				const feature = features.find(f => f.properties?.parcel_id === id)
				if (feature && feature.properties?.area_square_feet) {
					totalSqFt.value += Number(feature.properties.area_square_feet)
					totalSqMeter.value += Number(feature.properties.area_square_meters)
				}
			}
		},
		{ deep: true }
	)
})

onBeforeUnmount(() => {
	const map = mapRef.value
	if (map) {
		map.off("move", pulseMoveHandler)
		clearPendingCadastreSelectionRestore(map)
		clearPendingTransactionSourceLoadFilterRefresh(map)
		clearOpportunityParcelsHighlight(map)
	}

	teardownMapContainerResizeObserver()
	stopPendingTargetSyncPolling()
})

defineExpose({
	refreshProspectsLayer,
	clearSelections,
	getOpportunitiesInViewport,
	registerPendingTargetSync,
	selectionModeActive,
	exitSelectionMode,
})
</script>

<style scoped>
article {
	width: 100%;
	height: 100%;
	position: relative;

	#map {
		width: 100%;
		height: 100%;
		position: absolute;
		top: 0;
		left: 0;
		-webkit-touch-callout: none;
		user-select: none;
	}

	#draw-info {
		position: absolute;
		bottom: var(--spacing-md);
		right: calc(var(--spacing-md) + var(--size-button) + var(--spacing-sm));
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
		width: max-content;
		max-width: 240px;
		background-color: var(--neutral-0);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-md);
		padding: var(--spacing-sm) var(--spacing-md);
		box-shadow: var(--shadow-lg);
		font-family: var(--font-family-primary);
		z-index: 1000;

		.feature-type {
			font-weight: var(--font-weight-semibold);
			font-size: var(--font-size-xxs);
			letter-spacing: 0.04em;
			text-transform: uppercase;
			color: var(--neutral-700);
		}

		.value {
			font-size: var(--font-size-base);
			font-weight: var(--font-weight-semibold);
			color: var(--neutral-900);
		}
	}
}
</style>
